import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatInstant, formatDay, todayISO, diffDays, decodeDisplayColumns } from '@mononoke/shared';
import { linkBarcode } from '../platform/barcodes.js';
import { api, call } from '../platform/api.js';
import { cacheKey, cachedRead, readCache } from '../platform/read-cache.js';
import { PendingIntakeList } from '../components/pending-changes.js';
import { AreaSwitch, AreaWaitingBadge } from '../components/area-chip.js';
import { PickerBand } from '../components/picker-band.js';
import { ListSearchRow } from '../components/list-search-row.js';
import { HeaderActs } from '../components/header-acts.js';
import { SelectionBackGuard } from '../components/selection-back-guard.js';
import {
  rememberProjectNames,
  rememberCharacterNames,
  rememberedProjectName,
  rememberedCharacterName,
} from '../platform/name-cache.js';
import { useMaxWidth } from '../components/use-max-width.js';
import { leave, navigate, routePath, type Route } from '../platform/router.js';
import { photoQueue } from '../platform/photo-queue.js';
import { appendPage, EMPTY_PAGE_STATE, type PageState } from './items-paging.js';
import type { ItemSummary, ItemSort } from '../../../api/src/catalog/items.js';
import { BulkEdit } from '../components/bulk-edit.js';
import { BookingForm } from '../components/booking-form.js';
import { FilterSheet } from '../components/filter-sheet.js';
import { Toast, useToast } from '../components/toast.js';
import { buildTree, describeConfig, flattenRows, PHONE_MAX_LEVELS } from '../display/engine.js';
import { DisplayPill } from '../display/display-pill.js';
import { DisplaySheet } from '../display/display-sheet.js';
import { GroupedList } from '../display/grouped-list.js';
import { WINDOW_FROM_ROWS } from '../display/windowed-rows.js';
import { CompactRow } from '../display/compact-row.js';
import { usePrint } from '../display/use-print.js';
import { useSavedViews } from '../display/saved-views.js';
import { saveDisplayState, type StoredDisplay } from '../display/display-state.js';
import { useStoredFilters } from '../display/use-stored-filters.js';
import { DisplayBar } from '../display/display-bar.js';
import { DatasheetView } from '../display/datasheet.js';
import { initialItemsDisplay, itemColumns, itemEdge, itemsDisplaySpec, type ItemFieldDef, ITEMS_DISPLAY_DEFAULT, itemStatusTone } from './item-display.js';
import type { DisplayItemRow, ItemSummary as ItemSummaryType } from '../../../api/src/catalog/items.js';
import type { SortDirection } from '../components/sort-direction.js';
import {
  type ActiveFilters,
  STATUS_LABELS,
  countFilters,
  describeItemFilters,
  itemFilterDimensions,
  removeItemFilter,
  sanitizeItemFilters,
} from './item-filters.js';
import { FilterChips } from '../display/filter-chips.js';
import { FilterPicker } from '../display/filter-picker.js';
import type { IntakeMeta } from '../../../api/src/catalog/intake.js';
import { readIntakeMeta } from '../platform/intake-meta.js';
import type { SourceRow } from '../../../api/src/catalog/sources.js';
import { FILTER_NO_SOURCE, FILTER_NO_KIND } from './box-filters.js';
import { SelectBox, useCheckboxSelection, type CheckboxSelection } from '../components/select-box.js';
import { downloadExport } from '../platform/export.js';
import { consumeListPosition, rememberListPosition } from '../platform/list-position.js';
import { useLanguage } from '../platform/use-language.js';
import { NoteSheet } from '../components/note-sheet.js';
import { Sheet } from '../components/sheet.js';
import { PhotoAddButtons, PhotoStripThumbs } from '../components/photo-strip.js';
import { usePhotoUploads } from '../components/use-photo-uploads.js';
import { useConfirm } from '../components/confirm.js';
/**
 * THE LIST IS THE SCROLLER AT A DESK (0.1.179), the page is on a phone.
 *
 * At >=1024px `.item-list` has its own `overflow-y`, so the page itself does not scroll — that
 * is what makes the actions column beside it genuinely static, which sticky could never do
 * while content above the grid scrolled away. These three helpers are the seam: they act on
 * the list when it is the scroller and on the window when it is not, so drag-auto-scroll and
 * the remembered list position keep working at BOTH widths from one call site each.
 *
 * `scrollHeight > clientHeight` is the test rather than a media query, because it asks the
 * question that actually matters — is this element scrolling? — and cannot drift from the CSS.
 *
 * SINCE 0.1.487 THE DESK SHEET (`.ds-wrap`) IS A SCROLLER TOO — its own scrollbar beside the
 * rows, the maintainer's ask (8 Sep 2026) — so the seam takes whichever list element is mounted
 * (`scrollHost` below): the phone grouped list, the sheet, or the flat list.
 */
function listScrolls(el: HTMLElement | null): el is HTMLElement {
  return el !== null && el.scrollHeight > el.clientHeight + 1;
}

function listScrollTop(el: HTMLElement | null): number {
  return listScrolls(el) ? el.scrollTop : window.scrollY;
}

function scrollListTo(el: HTMLElement | null, y: number): void {
  if (listScrolls(el)) el.scrollTop = y;
  else window.scrollTo(0, y);
}


/** The bulk SANITY bound, MIRRORED (not imported) from MAX_BULK_ITEMS in
 * apps/api/src/catalog/bulk.ts: that module runtime-imports intake.ts (node:crypto),
 * which must never enter the browser bundle — same reason the status list is mirrored
 * in filter-sheet.tsx. 10,000 guards payloads, not humans (maintainer ruling, 27 Jul —
 * the old 100 made real cleanup passes impossible); it sits far above the catalogue. */
const MAX_BULK = 10_000;

/**
 * THE FILTER DIMENSIONS as list-query input — the ONE place the set of filters lives. Three
 * reads build this input (the list's own page, "Select all matching", and the filter sheet's
 * live count), and a dimension present in one but missing from another is a filter that
 * announces itself and does nothing — which happened once, with `withoutPhoto` (see the note at
 * its old call site). Collapsing the three copies into one function makes an omitted dimension
 * impossible rather than merely visible in review.
 *
 * ⚠️ SCOPE AND SEARCH ARE NOT HERE. `boxId` is not a user filter (it never appears as a chip and
 * cannot be cleared), and `search`/`applied` differ per call site; both stay explicit at each
 * caller so the deliberate difference is legible.
 */
function filterDims(active: ActiveFilters): {
  categoryId?: string;
  status?: string[];
  withoutPhoto?: true;
  withoutBarcode?: true;
  sourceIds?: string[];
  withoutSource?: true;
  sourceKinds?: string[];
  withoutSourceKind?: true;
  fields?: { key: string; value: string }[];
} {
  // The null sentinels are a CLIENT convention (one set can hold "Zalando" and "no source yet");
  // the wire has two shapes, a list of ids and a flag, because a null is not an id. Split here, in
  // the one place the wire shape is written, so no caller has to know the sentinel exists.
  const sources = (active.sources ?? []).filter((s) => s !== FILTER_NO_SOURCE);
  const noSource = (active.sources ?? []).includes(FILTER_NO_SOURCE);
  const kinds = (active.sourceKinds ?? []).filter((k) => k !== FILTER_NO_KIND);
  const noKind = (active.sourceKinds ?? []).includes(FILTER_NO_KIND);
  return {
    ...(active.category ? { categoryId: active.category.id } : {}),
    ...(active.status && active.status.length > 0 ? { status: active.status } : {}),
    ...(active.withoutPhoto === true ? { withoutPhoto: true } : {}),
    ...(active.withoutBarcode === true ? { withoutBarcode: true as const } : {}),
    ...(sources.length > 0 ? { sourceIds: sources } : {}),
    ...(noSource ? { withoutSource: true as const } : {}),
    ...(kinds.length > 0 ? { sourceKinds: kinds } : {}),
    ...(noKind ? { withoutSourceKind: true as const } : {}),
    ...(Object.keys(active.fields).length > 0
      ? { fields: Object.entries(active.fields).map(([key, f]) => ({ key, value: f.value })) }
      : {}),
  };
}

/**
 * ONE ROW, MEMOISED — and the memo is the entire point of this component existing.
 *
 * Field report, 30 Jul 2026: "mass selecting items causes a bit of lag." Measured cause:
 * `page.rows` ACCUMULATES across pages (items-paging.ts appends), so a scrolled session
 * holds up to 1,948 rows — and every row used to be inline JSX inside the map. React then
 * reconciled the whole list on each `setSelected`, roughly 20,000 elements per frame while
 * a finger was moving. The gesture was already rAF-throttled, so the cost was never event
 * frequency; it was re-rendering rows whose appearance had not changed.
 *
 * WHAT MAKES THE MEMO ACTUALLY WORK, and what silently breaks it: every prop here is a
 * primitive or a stable reference. The handlers are `useCallback(..., [])` in the parent
 * and read live state from refs, because a handler re-created each render makes
 * `React.memo` compare unequal every time and the whole optimisation evaporates with no
 * visible symptom other than the lag coming back. If you add a prop, it must be primitive
 * or stable — an inline object or arrow function here is a silent regression.
 *
 * `selected` is a BOOLEAN, not the Set. Passing the Set would change identity on every
 * selection and re-render all 1,948 rows again — the exact bug being fixed.
 */
/**
 * THE ROW'S SHAPE (0.1.501; maintainer, 9 Sep 2026: "the item list in the box details screen should
 * be the same as the item list in the items tab … the item rows were way too big and not full
 * bleed"). Inside a box the flat row wears the SHEET'S clothes — the desk datasheet's grid row
 * (`.ds-row`, 48px, the same five cells the tab draws) or the phone's compact row (`.gl-row`,
 * 64px, thumb · name · sub · date · state) — while staying the flat list: same paged read, same
 * selection, same back-context link. Ruled option 2 of three (design/box-outfit-batch): the sheet
 * components themselves are built for a full-height pane with their own scroller and sticky
 * head, and every one of those fights a page-scrolling detail. The Items TAB keeps 'flat' only
 * where it never shows the sheet (the picking modes); this is a second row implementation kept
 * in step with the sheet's — the third occurrence extracts it.
 */
type ItemRowShape = 'flat' | 'sheet' | 'compact';

/** The sheet's five box columns: Item · Category · Location · Deadline · State (a paged
 *  `ItemSummary` carries no source and no custom fields, so those two stay off). */
const BOX_SHEET_GRID = 'minmax(0, 2.2fr) minmax(0, 1.1fr) minmax(0, 1.1fr) minmax(0, 0.8fr) minmax(0, 0.9fr)';

/** The sheet's 3px state edge, from the status alone (`itemEdge` reads the same field). */
function edgeForStatus(status: string): string {
  const tone = itemStatusTone(status);
  if (tone === 'info') return 'var(--info-edge)';
  if (tone === 'warn') return 'var(--warn-edge)';
  if (tone === 'ok') return 'transparent';
  return 'var(--pending-soft)';
}

interface ItemRowProps {
  /** Already filed on the pick's target — rendered inert (0.1.396). */
  filed?: boolean;
  shape: ItemRowShape;
  row: ItemSummary;
  index: number;
  selecting: boolean;
  selected: boolean;
  /** A first click has landed here while a selection exists; the next one opens it.
   *  A BOOLEAN for the same reason `selected` is: passing the armed id would change
   *  identity for every row on every arm and undo the memo. */
  armed: boolean;
  /** The offline queue holds an unsent change for this garment (redesign 1c). A BOOLEAN,
   *  like `selected`/`armed`, so the memo holds: passing the queue itself would change
   *  identity for every row on every enqueue. */
  pending: boolean;
  thumbUrl: string | undefined;
  /** Set when this list is scoped to a box, so the row's href carries the same context
   *  `onRowClick` navigates with. Stable per list, so the memo still holds. */
  boxId: string | undefined;
  // Row and checkbox stopped being the same tap in 0.1.83: the checkbox owns selection
  // (and with it the drag and shift-click range gestures), the row opens the item. The
  // `index`/`shiftKey` this used to take were left behind by that split and had been dead
  // ever since — invisibly, because .tsx was outside every lint glob until 2 Aug 2026.
  onRowClick: (row: ItemSummary) => void;
  /** The shared selection (0.1.406) — the box owns the toggle, the drag and the shift range. */
  pick: CheckboxSelection;
  onThumbError: (photoKey: string) => void;
}

const ItemRow = memo(function ItemRow({
  row,
  index,
  selecting,
  selected,
  armed,
  pending,
  thumbUrl,
  boxId,
  onRowClick,
  pick,
  onThumbError,
  filed = false,
  shape,
}: ItemRowProps) {
  const rowClass = [
    'item-row',
    filed ? 'item-row-filed' : '',
    armed ? 'item-row-armed' : '',
    selected ? 'item-row-selected' : '',
    `item-row-status-${row.status}`,
  ]
    .filter(Boolean)
    .join(' ');
  const href = routePath(
    boxId === undefined ? { name: 'item', id: row.id } : { name: 'item', id: row.id, from: { kind: 'box', id: boxId } },
  );
  const onClick = (): void => {
    if (filed) return;
    onRowClick(row);
  };
  const lead = selecting && !filed ? <SelectBox id={row.id} label={`Select ${row.name}`} index={index} pick={pick} /> : null;
  const pendingPill = pending ? (
    <span className="item-pending" role="status">
      Waiting to send
    </span>
  ) : null;

  if (shape === 'sheet') {
    // The desk datasheet's row, cell for cell (item-display.tsx's `itemColumns`): thumb + link
    // + brand · category · location · deadline · state. `role="row"` inside a plain list is
    // presentational here — the list is not a table and has no head row semantics to keep.
    const today = todayISO();
    const overdue = row.returnDueOn !== null && row.returnDueOn < today;
    const soon = row.returnDueOn !== null && !overdue && diffDays(today, row.returnDueOn) <= 7;
    return (
      <li
        className={`${rowClass} ds-row`}
        data-status={row.status}
        data-row-index={index}
        style={{ gridTemplateColumns: BOX_SHEET_GRID, ['--row-edge' as string]: edgeForStatus(row.status) }}
        onClick={onClick}
      >
        <span className="ds-cell">
          {lead}
          <span className="ds-lead">
            {thumbUrl !== undefined ? (
              <img
                className="ds-thumb"
                src={thumbUrl}
                alt=""
                loading="lazy"
                onError={() => {
                  if (row.photoKey !== null) onThumbError(row.photoKey);
                }}
              />
            ) : (
              <span className="ds-thumb" aria-hidden="true" />
            )}
            <a className="item-link ds-name" href={href} onClick={(e) => e.preventDefault()}>
              {row.name}
            </a>
            {row.brand !== null && row.brand !== '' && <span className="ds-muted">· {row.brand}</span>}
            {pendingPill}
          </span>
        </span>
        <span className="ds-cell">
          {row.categoryPath === null ? <span className="ds-inert">—</span> : <span className="ds-muted">{row.categoryPath}</span>}
        </span>
        <span className="ds-cell">
          {row.locationName === null ? <span className="ds-inert">—</span> : <span className="ds-muted">{row.locationName}</span>}
        </span>
        <span className="ds-cell">
          {row.returnDueOn === null ? (
            <span className="ds-inert">—</span>
          ) : (
            <span className={overdue ? 'ds-deadline ds-deadline-overdue' : soon ? 'ds-deadline ds-deadline-soon' : 'ds-deadline'}>
              {overdue && <span className="ds-dot" aria-hidden="true" />}
              {formatDay(row.returnDueOn)}
            </span>
          )}
        </span>
        <span className="ds-cell">
          {row.status === 'flagged' && row.flagReason !== null ? (
            <span className="ds-pill-alert">{row.flagReason}</span>
          ) : (
            <span className={`ds-state ds-state-${itemStatusTone(row.status)}`}>{STATUS_LABELS[row.status] ?? row.status}</span>
          )}
        </span>
      </li>
    );
  }

  if (shape === 'compact') {
    // The phone's compact row (display/compact-row.tsx), by hand: lead · thumb · name + sub ·
    // date + state. The status line the flat row weaves is split the way the tab splits it.
    const tone = itemStatusTone(row.status);
    return (
      <li
        className={`${rowClass} gl-row`}
        data-status={row.status}
        data-row-index={index}
        style={{ ['--row-edge' as string]: edgeForStatus(row.status) }}
        onClick={onClick}
      >
        {lead}
        {thumbUrl !== undefined ? (
          <img
            className="gl-thumb"
            src={thumbUrl}
            alt=""
            loading="lazy"
            onError={() => {
              if (row.photoKey !== null) onThumbError(row.photoKey);
            }}
          />
        ) : (
          <span className="gl-thumb" aria-hidden="true" />
        )}
        <span className="gl-main">
          <a className="item-link gl-name" href={href} onClick={(e) => e.preventDefault()}>
            {row.name}
          </a>
          <span className="gl-sub">
            {row.status === 'flagged'
              ? `⚠ Flagged${row.flagReason !== null ? ` · ${row.flagReason}` : ''}`
              : row.status === 'checked_out'
                ? `Out with ${row.holderName ?? 'someone'}`
                : row.status === 'in_custody'
                  ? row.holderName !== null
                    ? `In ${row.holderName}'s custody`
                    : 'In custody'
                  : [row.brand, row.categoryPath, row.locationName].filter(Boolean).join(' · ') || '—'}
          </span>
        </span>
        <span className="gl-side">
          {row.returnDueOn !== null && <span className="gl-date">{formatDay(row.returnDueOn)}</span>}
          <span className={`gl-state gl-state-${tone}`}>{STATUS_LABELS[row.status] ?? row.status}</span>
        </span>
        {pendingPill}
      </li>
    );
  }

  return (
    <li
      className={[
        'item-row',
        // ALREADY ON THE TARGET (0.1.396) — greyed and inert while picking, the outfits picker's
        // rule. Offering it would be offering a refusal.
        filed ? 'item-row-filed' : '',
        armed ? 'item-row-armed' : '',
        // The redesign's selected treatment: accent-soft wash + a 3px inset accent rule.
        // A CLASS, not just the checkbox, because the row itself must read as picked when
        // forty of them are — a tick 300px from where the eye is scanning does not.
        selected ? 'item-row-selected' : '',
        // STATE IS AN EDGE, NOT A BADGE ON EVERY ROW (frame 14-2b, rule 6; 0.1.276): the
        // status drives a 3px left edge, and — on the phone — silences the *Available*
        // pill. 4,174 identical green pills was one of the five founding problems; the
        // exception is loud, the norm is silent. `data-status` also lets the CSS hide the
        // available pill without the row re-plumbing what a status means.
        `item-row-status-${row.status}`,
      ]
        .filter(Boolean)
        .join(' ')}
      data-status={row.status}
      data-row-index={index}
      // A real <a> wraps the row content below via CSS; the li-level handler keeps
      // the whole row tappable, which the 48px thumb alone is not on a phone.
      onClick={() => {
        if (filed) return;
        onRowClick(row);
      }}
    >
      {selecting && !filed && (
        /* The shared box (0.1.406) — same component the maintenance queue and the project /
           character subsections use, so the gesture cannot drift apart between them. */
        <SelectBox id={row.id} label={`Select ${row.name}`} index={index} pick={pick} />
      )}
      {thumbUrl !== undefined ? (
        <img
          className="item-thumb"
          src={thumbUrl}
          alt=""
          loading="lazy"
          onError={() => {
            // Expired URL or vanished object: back to the placeholder, never the
            // broken-image glyph. The next page load re-signs fresh URLs anyway.
            if (row.photoKey !== null) onThumbError(row.photoKey);
          }}
        />
      ) : (
        <span className="item-thumb" aria-hidden="true" />
      )}
      <span className="item-main">
        {/* A real link: middle-click, long-press and keyboard all work; the
            onClick keeps SPA navigation for the normal tap. */}
        <a
          className="item-link"
          // Agrees with `onRowClick` — a middle-click or "open in new tab" must carry the
          // same context the left-click does.
          href={routePath(boxId === undefined ? { name: 'item', id: row.id } : { name: 'item', id: row.id, from: { kind: 'box', id: boxId } })}
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          <strong>{row.name}</strong>
        </a>
        {/* THE WOVEN STATE META (frame 14-2b, S8): an exceptional row says its state on
            the meta line in the state tone — "Out with Léa · back 16 Aug", "⚠ Flagged ·
            cleaning required" — instead of brand·category. Available (and retired) rows
            keep the catalogue facts. The tone class pairs with the row's 3px state edge
            (rule 6): edge + text + word, never colour alone. */}
        {row.status === 'flagged' ? (
          <small className="item-meta-alert">
            ⚠ Flagged{row.flagReason !== null ? ` · ${row.flagReason}` : ''}
          </small>
        ) : row.status === 'checked_out' ? (
          <small className="item-meta-info">
            Out with {row.holderName ?? 'someone'}
            {row.returnDueOn !== null ? ` · back ${formatDay(row.returnDueOn)}` : ''}
          </small>
        ) : row.status === 'in_custody' ? (
          <small className="item-meta-info">
            {row.holderName !== null ? `In ${row.holderName}'s custody` : 'In custody'}
            {row.returnDueOn !== null ? ` · back ${formatDay(row.returnDueOn)}` : ''}
          </small>
        ) : (
          <small>{[row.brand, row.categoryPath, row.locationName].filter(Boolean).join(' · ')}</small>
        )}
      </span>
      {/* WAITING TO SEND (redesign 1c, level-3): this garment has an unsent change sitting in
          the offline queue. Its own pill, in a cool tone distinct from the warm status pills,
          because "in transit" is a different kind of fact from "available / out / flagged" —
          collapsing it into the status colour would read as a fourth status. Placed before the
          code so the status column still lines up across rows. */}
      {pending && (
        <span className="item-pending" role="status">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 12a9 9 0 1 1-3-6.7" />
            <path d="M21 4v4h-4" />
          </svg>
          Waiting to send
        </span>
      )}
      {/* The code column (redesign row anatomy): right-aligned, tabular, its own 88px
          track — a code is something you READ ACROSS rows when checking a rack, and codes
          buried under the meta line could not be scanned vertically. Empty renders the
          spacer so the status pills still line up. */}
      {/* THE ROW ENDS IN A CHEVRON (frame 14-2b, S9 ruling 15 Aug): the codes column and
          the status pill both left the list row. State is carried by the 3px edge and the
          woven meta (rule 6), so the pill was a third copy; the rack-scanning code stays
          readable on the item detail, where the frame keeps it. */}
      <span className="item-chevron" aria-hidden="true">
        ›
      </span>
    </li>
  );
});

/**
 * The item list — the app's home once signed in (intake-flow design v1.1 §3).
 *
 * THUMBNAILS, AND THE COMMENT THAT USED TO BE HERE. This block said no thumbnail existed
 * in R2 and no signing endpoint existed, so "every row shows a neutral placeholder — never
 * a 1.4 MB original: ten of those is 14 MB". Both halves became false — the worker job and
 * `photoUrls` both shipped — and the warning came true anyway: the list query preferred no
 * variant, its tie-break resolved to `items/original/…`, and the rows really did carry
 * full-size photos. The hazard was written down here and still happened, because the note
 * lived in the consumer and the defect was in the query.
 *
 * So the guarantee now lives at the scene: `catalog/items.ts` orders `position` first and
 * `variant = 'thumb'` second, and its test asserts the thumb wins. What is left here is the
 * consumer's own half — a missing or failed thumb renders a placeholder, never a broken
 * image, and a signing outage is quiet (see `fetchThumbUrls`).
 *
 * SEARCH IS DEBOUNCED AND GENERATION-GUARDED. The debounce (250 ms) keeps a fast typist
 * from issuing a query per keystroke. The generation counter is the important half: two
 * in-flight searches can resolve out of order, and without the guard the SLOWER response
 * wins the final render — showing the results of a query the user is no longer looking
 * at, with no error anywhere. Same family as the stale-read rules in CLAUDE.md: the
 * failure is silent and looks like data.
 *
 * FAILURE IS A STATE, NOT A BLANK (G10). An empty list can mean three things — truly no
 * items, no matches for this search, or a failed/stale-auth read. Each renders
 * differently, and the error case keeps the rows it already has plus a Retry: throwing
 * away loaded rows because page 4 failed would punish the user for our transport.
 */

/** Explicit page size — the server clamps anyway, but the client says what it means. */
const PAGE_SIZE = 50;

/** Active filters, AND-combined with search. Labels live beside ids because the chip
 * must say "Category: Outerwear ×", not a uuid. One value per dimension; picking again
 * replaces. */
// `ActiveFilters`, the status vocabulary and `countFilters` moved to `item-filters.ts` so the
// filter sheet can share them without a cycle (this file imports the sheet). Re-exported for the
// callers that still read the type from here.
export type { ActiveFilters };
const NO_FILTERS: ActiveFilters = { fields: {} };

/**
 * Whether a direction is the one the server would pick for that axis anyway (0.1.394).
 *
 * ⚠️ IT MIRRORS `defaultDirectionFor` IN `catalog/items.ts`, deliberately rather than importing it:
 * this file already imports the sort TYPE from the API package, but the rule matters here for a
 * different reason — deciding what NOT to put on the wire, and which first page may occupy the one
 * global cache slot. If the server's defaults ever change, this changes with them; the two are
 * pinned together by the API test that asserts an omitted direction equals the historical order.
 */
function sortDirIsDefault(sort: ItemSort, direction: SortDirection): boolean {
  return direction === (sort === 'recent' ? 'desc' : 'asc');
}

type LoadPhase = 'idle' | 'loading' | 'error';


/**
 * THE ITEM LIST'S ACTS, DEFINED ONCE (0.1.166).
 *
 * ⚠️ ONE DEFINITION, TWO SURFACES — a sticky column at a desk, a sheet on a phone. The
 * alternative considered and rejected was keeping the old selection-bar chips for phones
 * and the column for desks: two codepaths for one set of acts, which is exactly how the
 * duplicate outfits section on the bookings screen drifted before 0.1.158 removed it.
 *
 * `to` is a REDIRECT — a link to a screen rather than an act on the selection, so it stays
 * enabled with nothing selected. The two "Modify an existing…" redirects were removed on
 * instruction (4 Aug): the nav already reaches Reservations and Outfits, and a second route
 * to a tab is not an action on the garments you have just picked. `Go to Check-outs →`
 * remains because its group has no other content.
 */
interface ActContext {
  newReservation: () => void;
  bulkAt: (at: 'booking' | 'outfit' | 'retire' | 'restore' | 'order' | undefined) => void;
  pickOutfit: () => void;
  exportSelected: () => void;
  movement: (kind: 'out' | 'in') => void;
  deleteSelected: () => void;
}

type ActGroup = {
  heading: string;
  acts: {
    label: string;
    to?: 'reservations' | 'outfits' | 'checkouts';
    run?: (c: ActContext) => void;
    danger?: true;
    /** The add pair's look (0.1.531): `existing` solid green, `new` green outline. */
    kind?: 'existing' | 'new';
    busyLabel?: string;
    /** Disabled unless the selection holds at least one garment this act can move. */
    needs?: 'out' | 'in';
    /** Names its count — "Retire 12…" (decision 4: a destructive commit says its number). */
    counted?: true;
  }[];
};

const ACT_GROUPS: ActGroup[] = [
  {
    heading: 'Reservation',
    acts: [
      // Existing first, solid; new after it, outlined (0.1.531 — the desk bar's rule, in the sheet).
      { label: 'Add to an existing reservation', kind: 'existing', run: (c) => c.bulkAt('booking') },
      { label: 'Add to a new reservation', kind: 'new', run: (c) => c.newReservation() },
    ],
  },
  {
    heading: 'Outfit',
    acts: [
      { label: 'Add to an existing outfit', kind: 'existing', run: (c) => c.pickOutfit() },
      { label: 'Add to a new outfit', kind: 'new', run: (c) => c.bulkAt('outfit') },
    ],
  },
  {
    /**
     * ⚠️ THE BOOKING GROUP NOW HOLDS THE THREE ACTS THAT USED TO LIVE IN THE
     * SELECTION BAR (maintainer, 4 Aug). It carried a `MISSING:` line for one release
     * because the section had been named without contents; these are its contents.
     */
    heading: 'Items',
    acts: [
      /**
       * ⚠️ CHECK OUT AND CHECK IN ARE GONE FROM HERE — maintainer ruling, 7 Aug 2026, and it
       * is a CORRECTNESS fix rather than a tidy-up.
       *
       * Their words: *"now i check in items that were checked out a reservation and are now
       * checked in but the checked out reservation still exists."*
       *
       * That is exactly right, and it is the more dangerous direction. `setBookingStatus`
       * moves a booking to `out` by checking every item out with the booking's id stamped on
       * the movement, and back by checking in what the booking still holds. **An item checked
       * in from this list bypasses that**: the garment reads available, the booking stays
       * `out`, and the two disagree with nothing anywhere reporting it. The booking's own
       * return then skips the item — `setBookingStatus` only checks in what is still
       * `checked_out` with THIS booking's id — so the reservation can never close cleanly.
       *
       * ⚠️ THE API IS NOT REMOVED. `checkOutItem` / `checkInItem` are how a reservation moves
       * its items and are called by `setBookingStatus` on both transitions; deleting them
       * would delete the feature. **This closes the door, not the room** — which also means a
       * queued offline replay of an older press still works, and restoring the buttons is a UI
       * change rather than a migration.
       *
       * ORDER IS STILL THE MAINTAINER'S (5 Aug) for what remains: editing, then the two that
       * take a garment out of circulation and bring it back — kept adjacent because they are
       * each other's undo.
       */
      { label: 'Edit fields', run: (c) => c.bulkAt(undefined) },
      /**
       * RESTORE MOVED OUT OF THE BULK SHEET (5 Aug). It sat under "Restore retired" there,
       * one screen deeper than the Retire it undoes — so the act and its reversal lived in
       * different places. `bulkAt('restore')` opens the same sheet at the same confirm.
       *
       * ⚠️ RETIRE IS LAST (frame's acts sheet, 0.1.252) — the destructive act closes the
       * group, separated by position, its count in the label like the desk bar's. Restore
       * and Retire stay ADJACENT, which is what the 5 Aug ordering protected: they are
       * each other's undo.
       */
      { label: 'Restore', run: (c) => c.bulkAt('restore') },
      { label: 'Retire', run: (c) => c.bulkAt('retire'), danger: true, counted: true },
      /**
       * DELETE (maintainer, 19 Aug) — the harder-edged twin of Retire, which the selection could
       * do to boxes but not to items. A client loop over the single `deleteItem` (operate-tier
       * since migration 060), mirroring the boxes `bulkDelete`: an item that has ever moved, been
       * reserved, edited, or sits in a box REFUSES and is kept, and the refusal count is shown —
       * no silent drop (G10). There is no bulk-delete server mutation; there does not need to be.
       */
      { label: 'Delete', run: (c) => c.deleteSelected(), danger: true, counted: true },
      /**
       * ⚠️ EXPORT IS NOT HERE ANY MORE. Exporting a SELECTION was removed on instruction and
       * has no replacement — Settings exports the whole catalogue, which is the Guardrail 3
       * promise. A narrower file is a filter away in a spreadsheet, and that is the trade.
       */
    ],
  },
];

function ItemActs({
  ctx,
  noneSelected,
  exporting,
  movable,
  selectedCount,
  extra,
  extraHeading = 'Box',
  reservationOnly = false,
}: {
  ctx: ActContext;
  noneSelected: boolean;
  exporting: boolean;
  /** How many selected garments each movement can actually act on — see `eligibleFor`. */
  movable: { out: number; in: number };
  /** For the "(3)" hint: only shown when a movement applies to PART of the selection. */
  selectedCount: number;
  /**
   * ⚠️ THE CALLER'S OWN ACTS, AND THE REASON THIS PROP EXISTS AT ALL.
   *
   * `extraBulkActions` is how the BOX screen adds Keep and Returned for a selection. It
   * used to render inside the selection bar; 0.1.169 deleted that bar at every width and
   * left the slot rendering nowhere — a feature shipped one release earlier, silently
   * unmounted. `no-unused-vars` on the destructured prop is the only thing that caught it,
   * which is luck rather than a test.
   *
   * They render in the SAME place as every other act, so the desk column and the phone
   * sheet stay identical and the parity test keeps meaning something.
   */
  extra?: React.ReactNode;
  /** The heading over `extra` — the scope's own noun (0.1.527: it said "Box" inside a reservation). */
  extraHeading?: string;
  /**
   * A RESERVATION'S LIST OFFERS ONLY RESERVATION ACTS (0.1.527; maintainer, 2 Oct 2026: "the action
   * buttons that should show when an item is selected, should be 'Remove from this reservation'
   * 'Add to a new reservation' 'Add to an existing reservation'. The other action buttons should
   * not be available in the reservation screen"). The caller's own act leads, then the
   * Reservation group; Outfit, Edit, Restore, Retire and Delete are not offered here.
   */
  reservationOnly?: boolean;
}): JSX.Element {
  const groups = reservationOnly ? ACT_GROUPS.filter((g) => g.heading === 'Reservation') : ACT_GROUPS;
  const extraSection =
    extra !== undefined && extra !== null ? (
      <section className="item-acts-group">
        <h3>{extraHeading}</h3>
        <p className="item-acts-row">{extra}</p>
      </section>
    ) : null;
  return (
    <>
      {reservationOnly && extraSection}
      {groups.map((group) => (
        <section key={group.heading} className="item-acts-group">
          <h3>{group.heading}</h3>
          <p className="item-acts-row">
            {group.acts.map((act) => (
              <button
                key={act.label}
                type="button"
                className={
                  act.danger === true
                    ? 'btn-danger'
                    : act.kind === 'existing'
                      ? 'btn-primary'
                      : act.kind === 'new'
                        ? 'btn-outline-accent'
                        : undefined
                }
                disabled={
                  act.to === undefined &&
                  (noneSelected ||
                    (act.busyLabel !== undefined && act.needs === undefined && exporting) ||
                    // A movement with nothing to move is OFF rather than a prompt that ends
                    // in "0 done — 5 refused". This is the 5 Aug report, fixed at the door.
                    (act.needs !== undefined && movable[act.needs] === 0))
                }
                onClick={() => {
                  if (act.to !== undefined) navigate({ name: act.to });
                  else act.run?.(ctx);
                }}
              >
                {act.busyLabel !== undefined && act.needs === undefined && exporting
                  ? act.busyLabel
                  : act.needs !== undefined && movable[act.needs] > 0 && movable[act.needs] < selectedCount
                    ? `${act.label} (${String(movable[act.needs])})`
                    : act.counted === true && selectedCount > 0
                      ? `${act.label} ${String(selectedCount)}…`
                      : act.label}
              </button>
            ))}
          </p>
        </section>
      ))}
      {!reservationOnly && extraSection}
    </>
  );
}

export function Items({
  initialStatus,
  boxId,
  bookingId,
  forOutfit,
  forBooking,
  forCharacter,
  forProject,
  forBarcode,
  headExtra,
  headBelow,
  headAfterPrimary,
  headBelowSearch,
  extraBulkActions,
  onSelectionCount,
  clearSelectionGen,
  selectable,
  reservationActs,
  addNew,
  headTitleAct,
  inBand,
}: {
  initialStatus?: string[] | undefined;
  /**
   * PICK-FOR-A-LOOK (frame 5c "Add an existing item"). When set, this whole screen is a
   * picker: the title row names the outfit with a back to it, the bulk toolbar is suppressed,
   * and the primary act reads "Add N to {look}" — ticking rows and confirming files them into
   * the outfit via `addItemsToOutfit`, then returns to it. Orthogonal to `boxId` (never both).
   */
  forOutfit?: string | undefined;
  /**
   * PICK-FOR-A-RESERVATION (0.1.356) — the booking twin of `forOutfit`. When set, the whole Items
   * screen is a picker whose primary act files the ticked rows into that reservation via
   * `addBookingItems`, then returns to it. The header names the reservation with a back to it, the
   * caption says what ticking does. Orthogonal to `boxId`/`bookingId`/`forOutfit` (never two at once):
   * `bookingId` SCOPES the embedded list on the detail screen, `forBooking` PICKS from the whole
   * catalogue into that reservation.
   */
  forBooking?: string | undefined;
  /**
   * PICK-FOR-A-CHARACTER / PICK-FOR-A-PRODUCTION (0.1.395) — the character and project detail
   * screens' "Pick an existing item", the same mode again rather than a cut-down list of their
   * own. `forCharacter` needs `forProject` beside it (the route carries both so the back link can
   * rebuild the character URL); `forProject` alone files the garment on the production with no
   * character named, which is the project detail's own door.
   */
  forCharacter?: string | undefined;
  forProject?: string | undefined;
  /**
   * PICK-A-GARMENT-FOR-A-BARCODE (0.1.515) — scan's "Link to existing item" / "Link to another
   * item". ONE garment, not a set: the primary links the scanned code to the single ticked row
   * and returns to the scan ("Linked"). Opens on the "Barcode: none" filter (the backlog; a chip,
   * removable), and greys the garments that already carry this code.
   */
  forBarcode?: string | undefined;
  /**
   * SCOPE THIS LIST TO ONE BOX (035). When set, every read carries `boxId` and this
   * component IS the box detail's item list — maintainer, 4 Aug 2026: *"inside of every
   * box, there is a small item list, so it also needs to act as the item list in the item
   * tab."* Reusing the screen rather than copying its rows is the only way that stays
   * true: the drag gesture, shift-click ranges, select-all-matching, the bulk toolbar and
   * the memoised row are all here, and a second implementation would drift from this one
   * within a release.
   *
   * Three things it deliberately turns OFF — each of them a shared, GLOBAL slot that a
   * scoped list would poison:
   *   · the first-page read cache, whose key is `itemListFirstPage` for the whole app;
   *   · restoring the remembered list position on mount;
   *   · saving it on the way out.
   */
  boxId?: string | undefined;
  /**
   * SCOPE THIS LIST TO ONE RESERVATION (0.1.354) — the booking-detail twin of `boxId`. Every read
   * carries `bookingId` (server `listItems` filters to that booking's `booking_items`), and the
   * embedded treatment (no status/sort bar, no floating pill, no list-position save/restore) is the
   * same as a box. Orthogonal to `boxId`/`forOutfit` (never two at once).
   */
  bookingId?: string | undefined;
  /**
   * Rendered beside "+ Add new item". The box detail puts its Camera and Gallery buttons
   * here so the three ways of putting something INTO a box sit together (maintainer,
   * 5 Aug 2026) rather than in a separate block above the list.
   */
  headExtra?: React.ReactNode;
  /**
   * Rendered directly UNDER the sticky header, in normal flow. The box detail puts its photo
   * upload thumbnails here (0.1.177): inside the header every upload inflated the sticky
   * row's height — and therefore the actions column's measured offset — for as long as the
   * thumbnails showed. Progress belongs on the page, not in the frame.
   */
  headBelow?: React.ReactNode;
  /**
   * Rendered AFTER the primary "+ Add new item" (0.1.455). The box detail puts its desk
   * "Photos as new items" button here — the same place the catalogue's own sits — so the
   * two tabs read alike (maintainer, 3 Sep).
   */
  headAfterPrimary?: React.ReactNode;
  /**
   * Rendered BELOW the search row (0.1.523; it was above at 0.1.522). The box and reservation
   * details put their PHONE add pair here (`PhotoAddButtons`, compact) — the maintainer, 1 Oct
   * 2026: "move the … buttons on mobile to below the search bar". The catalogue draws its own
   * pair in the same place.
   */
  headBelowSearch?: React.ReactNode;
  /**
   * Extra buttons for the selection bar, given the current selection and a callback to
   * report what happened. The box detail uses it for Keep and Returned on ITEMS.
   *
   * A SLOT rather than exported selection state, deliberately: the selection is this
   * component's own — it is written by three gestures and read by five things here — and
   * lifting it out to let a parent add two buttons would put the list's most-touched state
   * somewhere the list does not control.
   */
  extraBulkActions?: ((itemIds: string[], onDone: (summary: string) => void) => React.ReactNode) | undefined;
  /**
   * Told how many rows are ticked, whenever that changes (0.1.523). Not the selection itself — the
   * slot rule above still holds — only its SIZE, so a parent that floats its own "Actions" button
   * can stand it down while this list's "N Actions" is up. Maintainer, 1 Oct 2026: "when I select
   * items, the big actions button gets replaced with the actions button for the selected items" —
   * two buttons in one floating stack was the reservation's phone screen until then.
   */
  onSelectionCount?: ((count: number) => void) | undefined;
  /**
   * Bump to drop the ticks (0.1.526). The reservation's Items band now HIDES the list when folded
   * instead of unmounting it (`CollapsibleSection keepMounted`), and the unmount was what used to
   * clear a selection on fold. A hidden list must not keep ticks nobody can see, so the parent
   * bumps this on fold and the effect below clears — the same outcome as before, minus the reload.
   */
  clearSelectionGen?: number | undefined;
  /**
   * `false` = no checkboxes at all (0.1.527; maintainer, 2 Oct 2026: "the selection boxes for the
   * item and outfits row in checked out and confirmed reservations need to be gone, because I
   * should not be changing anything at those reservation statuses"). Default true.
   */
  selectable?: boolean | undefined;
  /**
   * THE RESERVATION'S LIST (0.1.527): its selection offers only "Remove from this reservation"
   * (`extraBulkActions`) and the two reservation acts — on the desk in a row BELOW THE SEARCH
   * (with its doors, not above it), on the phone in the same sheet. Its title row is gone at
   * every width ("so that we don't have the acts above the search"). `ItemActs.reservationOnly`.
   */
  reservationActs?: boolean | undefined;
  /**
   * The scoped list's "+ Add new item" (0.1.523). A function REPLACES the plain `/add` navigation —
   * a reservation's new garment must join the reservation, and until 0.1.523 the desk primary
   * inside a reservation opened `/add` with no booking at all, so the garment landed in the
   * catalogue and nowhere else. `null` HIDES it: a confirmed reservation takes nothing new.
   * Undefined keeps the catalogue/box behaviour.
   */
  addNew?: (() => void) | null | undefined;
  /** Rendered right after the title and its count (0.1.523) — the reservation's green "+ Add
   *  existing item" (maintainer, 1 Oct 2026: "in the items title row next to the X amount of
   *  garments text"). */
  headTitleAct?: React.ReactNode;
  /**
   * THE LIST SITS UNDER A PARENT'S FOLDING BAND (0.1.524 — the reservation's Items band; maintainer,
   * 2 Oct 2026: "Make the items title row the same kind of design as the other subsection header
   * rows … a band that I can collapse and expand … remove the garment text … there's no need for it
   * to say '1 item at the end of the list'"). The band carries the title, the count and the act, so
   * this list drops its own title and count (the row's desk acts stay; on the phone the row has
   * nothing left and goes), and the end-of-list line leaves the screen — kept off-screen, not
   * removed: it is the suite's settle signal and a screen reader's "the list ended".
   */
  inBand?: boolean;
} = {}): JSX.Element {
  // Re-render when the language changes; `t` itself is a plain call (use-language.ts).
  useLanguage();
  /** The embedded-list scope: a box OR a reservation. `undefined` = the main Items tab. All the
   *  "is this a scoped/embedded list" checks read this, so box and booking behave the same. */
  const scopeId = boxId ?? bookingId;
  /**
   * WHERE YOU LEFT OFF. Read ONCE, at mount, and consumed — see `list-position.ts` for why
   * a later arrival at this screen must get a clean list rather than an inherited search.
   * `useState`'s initialiser form, not a bare call: this must run on the first render only,
   * or every re-render would try to consume a position that is already spent.
   */
  // Not consumed by the barcode picker (0.1.515): it opens on its own filter, and a remembered
  // search from the Items tab would hide the very garment somebody is holding.
  const [restored] = useState(() => (scopeId === undefined && forBarcode === undefined ? consumeListPosition() : null));
  const [query, setQuery] = useState(restored?.query ?? '');
  /** The search actually applied, post-debounce. Kept separate so the input never lags. */
  const [applied, setApplied] = useState(restored?.applied ?? '');
  const [page, setPage] = useState<PageState>(EMPTY_PAGE_STATE);
  const [phase, setPhase] = useState<LoadPhase>('loading');
  const [problem, setProblem] = useState<string | null>(null);
  /** Epoch ms of the cached copy being shown; null when the list is live. */
  const [staleSince, setStaleSince] = useState<number | null>(null);
  /** Epoch ms of the last successful load, live OR cached — the band's "Saved copy from …"
   *  time when the copy is live-but-now-offline (staleSince is null then). An INSTANT, so
   *  `Date.now()` is correct and no calendar-date helper applies (see date.ts). */
  const [lastLoadedAt, setLastLoadedAt] = useState<number | null>(null);
  /** Live offline state (redesign 1c): the band appears EAGERLY when the device drops the
   *  network, not only on a stale cache HIT. Seeded from navigator so a screen mounted
   *  already-offline shows it without waiting for an event. */
  const [isOffline, setIsOffline] = useState<boolean>(
    () => typeof navigator !== 'undefined' && navigator.onLine === false,
  );
  /** Bumped on every queue change so the band's count and the per-row pills re-render — the
   *  queue is the source of truth, this is just the subscription's nudge. */
  const [, bumpQueueTick] = useState(0);
  /**
   * key -> presigned URL. Keyed by object key, which is tenant-stable, so entries from a
   * superseded search are still correct for the same photo — no generation guard needed
   * here, unlike the row loads. `failed` reverts a broken image to the placeholder: an
   * expired URL or a deleted object must degrade to text-first, never to a broken-image
   * glyph.
   */
  const [armed, setArmed] = useState<string | null>(null);
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});
  const [failedThumbs, setFailedThumbs] = useState<ReadonlySet<string>>(new Set());

  /** Bulk-edit selection. Selection mode repurposes the row tap (toggle, not
   * navigate); leaving the mode always clears the set — a selection that survives
   * invisibly would make the next "Edit N" sentence claim rows nobody can see. */
  /**
   * PERMANENT SELECT MODE — maintainer, 30 Jul 2026: "the items screen should just be in
   * select mode always... so also remove the select button."
   *
   * Kept as a constant rather than deleted, because `selecting` still means something to
   * the row and the gesture code: it is the answer to "does a checkbox exist on this row",
   * which is now always yes. Removing the concept would inline `true` into a dozen places
   * and lose the name for what they are asking.
   */
  const selecting = selectable ?? true;


  /**
   * LIVE STATE FOR THE STABLE HANDLERS. Every callback passed to `ItemRow` is
   * `useCallback(..., [])`, because a handler re-created each render defeats the row's
   * `React.memo` and brings the mass-select lag straight back — silently, with no symptom
   * but the slowness. A handler with no deps cannot close over `selected`, `page.rows` or
   * `selecting`, so it reads them here instead. These are written on every render, so a
   * handler always sees the current value.
   */
  // Initialised empty rather than from `selected`: the hook that owns `selected` is called
  // just below (it needs `rowsRef`), and `selectedRef.current = selected` a few lines on fills it
  // on this same render.
  const selectedRef = useRef<ReadonlySet<string>>(new Set());
  const rowsRef = useRef<ItemSummary[]>([]);

  /**
   * ⚠️ THE MECHANISM MOVED OUT (0.1.406) — checkbox, drag and shift-range now live in
   * `components/select-box.tsx`, and this screen is one of FOUR callers. It was hoisted because
   * the maintenance queue and the project/character subsections had to behave IDENTICALLY
   * (*"it needs to be the exact same"*, maintainer 27 Aug), and four lists that merely agreed on
   * the day they were written would drift. Nothing about this screen's behaviour changed; the
   * code simply has one home now, and `select.spec`'s eleven tests still guard it.
   */
  const pick = useCheckboxSelection(
    () => rowsRef.current,
    () => scrollHost(),
  );
  const { selected, setSelected } = pick;
  const selectingRef = useRef(false);
  const queryRef = useRef('');
  const appliedRef = useRef('');
  const filtersRef = useRef<ActiveFilters>(NO_FILTERS);
  const sortRef = useRef<ItemSort>('name');
  /** The sort DIRECTION, carried in a ref beside the axis for the same reason (0.1.394). */
  const dirRef = useRef<SortDirection>('asc');
  /** Read by `onRowClick`, which is `useCallback(…, [])` and so cannot close over a prop. */
  const boxIdRef = useRef<string | undefined>(undefined);
  /** The reservation this list is embedded in, read by the same `useCallback([])` row-click
   *  (0.1.524) — so a garment opened inside a reservation carries it as its origin. */
  const bookingIdRef = useRef<string | undefined>(undefined);
  /** Like `boxIdRef` but for ANY embedded scope (box or booking) — the list-position save must be
   *  skipped for both, so the `useCallback([])` row-click reads this rather than `boxIdRef`. */
  const scopedRef = useRef<string | undefined>(undefined);
  /** The row a first click has ARMED, when a selection is protecting the list. A ref for
   *  the dependency-free handler; the state twin is only there to draw the hint. */
  const armedRow = useRef<string | null>(null);
  const restoredScroll = useRef(false);
  selectedRef.current = selected;
  /**
   * PICK-MODE state (frame 5c / "Picking garments for this reservation"). The target's NAME for the
   * header and the "Add N to …" label — read once from the list read, falling back to a generic name
   * until it lands so the header is never blank. `addingToPick` disarms the primary while the add is
   * in flight so a double-tap cannot file twice.
   *
   * ONE PICK TARGET (0.1.356): a look OR a reservation. The two modes share every bit of the picker
   * chrome — only the name read, the primary's mutation, and where it returns differ.
   */
  const pickTarget = forOutfit ?? forBooking ?? forCharacter ?? forProject ?? forBarcode;
  const picking = pickTarget !== undefined;
  /** Which of the five the mode is — read once so the noun, the mutation and the return route
   *  cannot disagree about what is being picked for. */
  const pickKind: 'outfit' | 'booking' | 'character' | 'project' | 'barcode' | null =
    forBarcode !== undefined
      ? 'barcode'
      : forOutfit !== undefined
      ? 'outfit'
      : forBooking !== undefined
        ? 'booking'
        : forCharacter !== undefined
          ? 'character'
          : forProject !== undefined
            ? 'project'
            : null;
  const [pickName, setPickName] = useState<string | null>(
    // ⚠️ SEEDED, so the chip does not read "this project" for a beat and then rewrite itself into
    // the project's name (maintainer, 26 Aug). See platform/name-cache.
    // A barcode has no name to read: the code itself is what the header names.
    forBarcode !== undefined
      ? `barcode ${forBarcode}`
      : forCharacter !== undefined
        ? rememberedCharacterName(forCharacter)
        : rememberedProjectName(forProject),
  );
  const [addingToPick, setAddingToPick] = useState(false);
  useEffect(() => {
    if (pickTarget === undefined) return;
    let live = true;
    void (async () => {
      if (forOutfit !== undefined) {
        const res = await call('listOutfits', () => api.listOutfits.query());
        if (live && res.ok) {
          const found = res.data.find((o) => o.id === forOutfit);
          if (found !== undefined) setPickName(found.name);
        }
      } else if (forBooking !== undefined) {
        const res = await call('listBookings', () => api.listBookings.query());
        if (live && res.ok) {
          const found = res.data.find((b) => b.id === forBooking);
          if (found !== undefined) setPickName(found.title);
        }
      } else if (forCharacter !== undefined && forProject !== undefined) {
        const res = await call('listCharacters', () => api.listCharacters.query({ projectId: forProject }));
        if (live && res.ok) {
          rememberCharacterNames(res.data);
          const found = res.data.find((c) => c.id === forCharacter);
          if (found !== undefined) setPickName(found.name);
        }
      } else if (forProject !== undefined) {
        const res = await call('listProjects', () => api.listProjects.query());
        if (live && res.ok) {
          rememberProjectNames(res.data);
          const found = res.data.find((x) => x.id === forProject);
          if (found !== undefined) setPickName(found.name);
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [pickTarget]);
  // "outfit", not "look" (maintainer, 25 Aug — one word for the thing, everywhere).
  const PICK_NOUNS = {
    outfit: 'outfit',
    booking: 'reservation',
    character: 'character',
    project: 'project',
    barcode: 'barcode',
  } as const;
  const pickNoun = pickKind === null ? 'outfit' : PICK_NOUNS[pickKind];
  // Falls back to a generic name until the read lands, so the header/primary are never blank.
  const pickLabelName = pickName ?? `this ${pickNoun}`;
  /**
   * ALREADY ON THIS TARGET (0.1.396) — greyed out and inert, the rule the outfits picker has had
   * since 0.1.392. The set is read from the SERVER's own scoped list rather than derived here:
   * "on this production" is a union of four branches (075) that the client cannot reproduce, and a
   * half-right answer would grey the wrong rows.
   */
  const [filedIds, setFiledIds] = useState<ReadonlySet<string>>(new Set());
  /**
   * ⚠️ A FILED ROW IS NEVER TICKED (0.1.528). It has no box, but the drag and the shift-range walk
   * every DRAWN row (`useCheckboxSelection` over `rowsRef`), and "Select all" asks the server for
   * every match — both could rope an already-filed garment in, and then "Add N" would count a row
   * that adds nothing. The selection is trimmed the moment one appears, so the number on the act is
   * the number that will be written. (The flat list's `.item-row-filed` pointer-events were the only
   * guard before, and they never stopped a range.)
   */
  useEffect(() => {
    if (filedIds.size === 0) return;
    if (![...selected].some((id) => filedIds.has(id))) return;
    setSelected(new Set([...selected].filter((id) => !filedIds.has(id))));
    // `setSelected` is the selection hook's stable setter.
  }, [selected, filedIds]);
  useEffect(() => {
    // THE BARCODE'S GARMENTS (0.1.515): the rows that already carry the code are greyed — linking
    // it to them again is a no-op, and offering it would be offering nothing. Read from the same
    // lookup the scan used, so "already carries it" means what the scan says it means.
    if (forBarcode !== undefined) {
      let live = true;
      void (async () => {
        const res = await call('findAllByCode', () => api.findAllByCode.query({ code: forBarcode }));
        if (live && res.ok) setFiledIds(new Set(res.data.items.map((i) => i.id)));
      })();
      return () => {
        live = false;
      };
    }
    if (forCharacter === undefined && forProject === undefined && forBooking === undefined) return;
    let live = true;
    void (async () => {
      const res = await call('listItems', () =>
        api.listItems.query(
          forCharacter !== undefined
            ? { characterId: forCharacter, idsOnly: true }
            : forProject !== undefined
              ? { projectId: forProject, idsOnly: true }
              : // THE RESERVATION'S OWN GARMENTS (0.1.523), greyed in its picker — adding one again
                // is a no-op the server already reports as "already in it".
                { bookingId: forBooking!, idsOnly: true },
        ),
      );
      if (live && res.ok) setFiledIds(new Set(res.data.ids ?? []));
    })();
    return () => {
      live = false;
    };
  }, [forCharacter, forProject, forBooking, forBarcode]);

  /** WHERE THE BACK CHIP GOES — the screen the pick was started from, in one place so the link
   *  and the post-commit navigate cannot send somebody to two different places. */
  const pickBackTo: Route =
    forBarcode !== undefined
      ? { name: 'scan' }
      : forOutfit !== undefined
      ? { name: 'outfit', id: forOutfit }
      : forBooking !== undefined
        ? { name: 'booking', id: forBooking }
        : forCharacter !== undefined && forProject !== undefined
          ? { name: 'character', projectId: forProject, id: forCharacter }
          : forProject !== undefined
            ? { name: 'project', id: forProject }
            : { name: 'items' };
  /** Pick mode's primary is armed: a set for the four filing modes, exactly ONE garment for a
   *  barcode (a link names one item; two identical tops are linked one scan at a time). */
  const pickReady = forBarcode !== undefined ? selected.size === 1 : selected.size > 0;
  const addLabel = addingToPick
    ? forBarcode !== undefined
      ? 'Linking…'
      : 'Adding…'
    : forBarcode !== undefined
      ? selected.size > 1
        ? 'Tick only one item'
        : `Link barcode to this item`
      : selected.size === 0
      ? `Add to ${pickLabelName}`
      : forBooking !== undefined
        ? // "Add X items to NAME OF RESERVATION" — the maintainer's words for this button (0.1.523).
          `Add ${String(selected.size)} item${selected.size === 1 ? '' : 's'} to ${pickLabelName}`
        : `Add ${String(selected.size)} to ${pickLabelName}`;
  const addSelectedToPick = async (): Promise<void> => {
    if (pickTarget === undefined || !pickReady || addingToPick) return;
    setAddingToPick(true);
    if (forBarcode !== undefined) {
      // ONE LINK, then back to the scan with the garment as the answer (maintainer ruling, 27 Sep
      // 2026 — the rack round is scan → pick → scan). A failure stays here with the tick intact
      // and says why; `linkBarcode` has already ledgered and notified (G10).
      const itemId = [...selected][0]!;
      const res = await linkBarcode(itemId, forBarcode);
      setAddingToPick(false);
      if (res.ok) navigate({ name: 'scan', linked: { code: res.data.code, itemId } }, { replace: true });
      else setProblem(`The barcode could not be linked: ${res.failure.message}`);
      return;
    }
    // On success, back to the target — its list now carries them. A failure is surfaced by `call`
    // (ledger + toast, G10); we stay put so the still-armed selection can be retried.
    // ⚠️ CLEAR, THEN LEAVE (0.1.527). While rows are ticked the selection's Back guard holds a history
    // sentinel on top; `leave` waits for that sentinel's pop. Clearing unmounts the guard, whose
    // cleanup pops it — without the clear, `leave` would time out and REPLACE this entry instead of
    // going back to where the errand started.
    const leaveCleared = (route: Route): void => {
      setSelected(new Set());
      leave(route);
    };
    if (forOutfit !== undefined) {
      const res = await call('addItemsToOutfit', () =>
        api.addItemsToOutfit.mutate({ outfitId: forOutfit, itemIds: [...selected] }),
      );
      setAddingToPick(false);
      if (res.ok) leaveCleared({ name: 'outfit', id: forOutfit });
    } else if (forBooking !== undefined) {
      const res = await call('addBookingItems', () =>
        api.addBookingItems.mutate({ bookingId: forBooking, itemIds: [...selected] }),
      );
      setAddingToPick(false);
      if (res.ok) leaveCleared({ name: 'booking', id: forBooking });
    } else if (forCharacter !== undefined && forProject !== undefined) {
      /**
       * ⚠️ ONE CALL PER GARMENT, because the door binds ONE (075) — and each is reported on its
       * own. A partial success is the honest outcome here: three of five filed is three filed,
       * and returning to the character with no message would leave the two failures invisible,
       * which is exactly the silent-drop Guardrail 10 forbids.
       */
      const failures: string[] = [];
      for (const itemId of selected) {
        const res = await call('addItemCharacter', () =>
          api.addItemCharacter.mutate({ itemId, characterId: forCharacter }),
        );
        if (!res.ok) failures.push(res.failure.message);
      }
      setAddingToPick(false);
      if (failures.length === 0) leaveCleared({ name: 'character', projectId: forProject, id: forCharacter });
      else setProblem(`${String(failures.length)} of ${String(selected.size)} did not file — ${failures[0] ?? ''}`);
    } else if (forProject !== undefined) {
      const failures: string[] = [];
      for (const itemId of selected) {
        const res = await call('addItemProject', () => api.addItemProject.mutate({ itemId, projectId: forProject }));
        if (!res.ok) failures.push(res.failure.message);
      }
      setAddingToPick(false);
      if (failures.length === 0) leaveCleared({ name: 'project', id: forProject });
      else setProblem(`${String(failures.length)} of ${String(selected.size)} did not file — ${failures[0] ?? ''}`);
    }
  };
  const [bulkOpen, setBulkOpen] = useState(false);
  /** When set, the bulk sheet opens straight at that act — `retire` at its confirm,
   * `booking` at the booking picker (which booking is a choice, not a default). */
  const [bulkOpenAt, setBulkOpenAt] = useState<'retire' | 'restore' | 'booking' | 'outfit' | 'order' | undefined>(
    undefined,
  );
  /** The new-booking form, opened from the selection. */
  const [bookingFormOpen, setBookingFormOpen] = useState(false);
  /** The "add to an existing outfit" picker, and the kits it offers. */
  const [outfitPickerOpen, setOutfitPickerOpen] = useState(false);
  const [outfitChoices, setOutfitChoices] = useState<{ id: string; name: string }[] | null>(null);
  /** The phone's acts sheet. At a desk the column is always visible and this stays shut. */
  const [actsSheetOpen, setActsSheetOpen] = useState(false);
  /* The bulk bar's More ▾ popover (redesign decision 3) and its `moreOpen` state LEFT at the
     3 Oct 2026 ruling ("all the actions should be displayed"): its three acts are inline now. */
  /** On the phone the selection bar is the compact count · Actions (n) · Clear (1b), and Clear
   * drops the "Esc" a thumb cannot press. 700 since 0.1.530 — the full row (and its Esc) starts at
   * 701, the same line `.items-bulkbar`'s CSS draws; it was 1023 while 701–1023 kept the compact face. */
  const compact = useMaxWidth(700);
  /** The floating action stack (S7, frame 2b) is the PHONE face: below 700 the acts leave
   *  the header for a floating `Actions · N` pill above the `+ New item` primary, and the band
   *  gains `Cancel selection`. 700 is the app's phone line (the `.float-*` primitive's own
   *  media). Only the main Items tab floats — the box-scoped reuse keeps its fixed acts bar. */
  const phone = useMaxWidth(700);

  /**
   * CATALOGUE PHOTOS-AS-NEW-ITEMS (0.1.345). The Items tab gets the same two-box add-split the box
   * detail has — "+ New item" and a "Photos as new items" camera zone — replacing the floating
   * pill. Only on the MAIN tab (no `boxId`, not the look-picker): a box has its own box-scoped
   * upload, and the picker mode adds nothing. Each settled photo becomes one new catalogue item via
   * `addPhotoItems` (the box-less twin of `addBoxPhotoItems`). Hooks run unconditionally; the wiring
   * below no-ops when `catalogueAdd` is false. */
  const catalogueAdd = scopeId === undefined && pickTarget === undefined;
  const { photos, addFiles, removePhoto, retryPhoto } = usePhotoUploads();
  const [attachingItems, setAttachingItems] = useState(false);
  /** The bulk-delete confirm (frame's `useConfirm`, not window.confirm). */
  const [confirmEl, ask] = useConfirm();

  /** Every act in the column needs a selection; the redirects do not. */
  const noneSelected = selected.size === 0;
  useEffect(() => {
    onSelectionCount?.(selected.size);
    // `onSelectionCount` is a parent's setter; the size is the only trigger.
  }, [selected.size]);
  useEffect(() => {
    if (clearSelectionGen !== undefined && clearSelectionGen > 0) setSelected(new Set());
    // Only a bump clears; `setSelected` is the selection hook's stable setter.
  }, [clearSelectionGen]);

  // WHAT IS WAITING TO SEND (redesign 1c). The garments with an unsent change, from the
  // queue's own ops — `ops()` carries the payload (the render-shaped `entries()` deliberately
  // does not). Only queued/sending count as "waiting"; failed/expired are the badge's
  // "needs attention", a different sentence. `intake` ops create a garment that has no row
  // here yet, so they have no itemId to light up — they are skipped, not force-cast.
  const pendingItemIds = new Set<string>();
  for (const op of photoQueue().ops()) {
    if (op.status !== 'queued' && op.status !== 'sending') continue;
    if (op.kind === 'movement' || op.kind === 'flag' || op.kind === 'photo-upload') {
      pendingItemIds.add(op.payload.itemId);
    }
  }
  const pendingCount = photoQueue().pendingCount();
  // The band labels the SHOWN rows as a saved copy. Show it when there are rows AND the copy
  // is not live — offline (eager), or a known stale cache hit. Gating on rows-present is what
  // keeps a COLD cache (offline, no rows, an error alert) from also flying the band, and what
  // keeps it hidden while live: offline-shell.spec pins both of those.
  const showStaleBand = page.rows.length > 0 && (isOffline || staleSince !== null);
  const savedCopyAt = staleSince ?? lastLoadedAt;

  /**
   * WHICH SELECTED GARMENTS A MOVEMENT CAN ACTUALLY ACT ON.
   *
   * Reported 5 Aug with a screenshot: five available items, Check in offered, a prompt for a
   * reason, and then "0 checked in ✓ — 5 refused". The server was right every time; the
   * SCREEN asked a question whose answer was always no, and charged a prompt for it.
   *
   * ⚠️ A SELECTION IS USUALLY MIXED, so this filters rather than disables on the worst case:
   * check in acts on the checked-out ones, check out on the rest, and the other half of the
   * selection is left alone and COUNTED rather than silently ignored.
   *
   * ⚠️ AND `selected` CAN OUTRUN THE LOADED ROWS — select-all-matching picks the whole
   * filtered set, not just the page in hand. An id with no row here is passed through: the
   * server is the authority on status and refuses honestly, which is a better answer than
   * this screen guessing from data it does not have.
   */
  const statusById = new Map(page.rows.map((r) => [r.id, r.status]));
  const eligibleFor = (kind: 'out' | 'in'): string[] =>
    [...selected].filter((id) => {
      const status = statusById.get(id);
      if (status === undefined) return true;
      return kind === 'out' ? status !== 'checked_out' : status === 'checked_out';
    });

  /** What the acts DO — passed to whichever surface renders them, so the column and the
   *  phone sheet cannot drift apart. Closing the sheet is part of every act: leaving it
   *  open over the thing it just changed is how a second tap hits the wrong row. */
  /**
   * CHECK OUT / IN ACROSS THE SELECTION.
   *
   * ⚠️ SEQUENTIAL, NOT `Promise.all`. Each call writes a movement row and re-reads the item
   * in its own transaction; firing twenty at once at one Postgres connection pool is how a
   * bulk act turns into a spray of timeouts that look like product defects. Slower on
   * purpose.
   *
   * ⚠️ AND IT REPORTS WHAT ACTUALLY HAPPENED (G10). A garment already out cannot be checked
   * out again and the server refuses it — that is a normal outcome on a selection of twenty,
   * not an error to swallow. "12 checked out ✓ — 3 refused" is the truth; "12 checked out ✓"
   * with three silently skipped is the failure this project is organised against.
   */
  /** Which bulk movement's note sheet is open ('out' | 'in'). The sheet replaced the
   * `window.prompt` (§4.1, 0.1.266) and keeps its contract: Close aborts, an empty
   * field proceeds without a note. */
  const [movementAsk, setMovementAsk] = useState<'out' | 'in' | null>(null);

  const runMovement = async (kind: 'out' | 'in', note: string | undefined): Promise<void> => {
    const ids = eligibleFor(kind);
    const skipped = selected.size - ids.length;
    if (ids.length === 0) return;
    /**
     * ONE NOTE FOR THE WHOLE SELECTION. The per-item card this replaced asked for a
     * note and wrote it onto the movement row — `movements` is append-only (D4) and that
     * note is often the only record of WHY a garment left. Dropping the ask when the act
     * became bulk would have quietly degraded the ledger, so it is asked once (the
     * NoteSheet above) and applied to every row.
     */
    setProblem(null);
    let done = 0;
    let queued = 0;
    const refused: string[] = [];
    for (const id of ids) {
      const input = { itemId: id, note, idempotencyKey: crypto.randomUUID() };
      const result =
        kind === 'out'
          ? await call('checkOutItem', () => api.checkOutItem.mutate(input))
          : await call('checkInItem', () => api.checkInItem.mutate(input));
      if (result.ok) {
        done += 1;
      } else if (result.failure.kind === 'unavailable') {
        /**
         * ⚠️ TRANSPORT FAILURE QUEUES — and NOT doing this was a real defect in the first
         * version of this act (caught by `offline-shell.spec`, 4 Aug).
         *
         * `call()` does not queue by itself; the detail screen's card enqueued explicitly,
         * and when the act moved here that half was left behind. Offline the loop simply
         * counted every garment as "refused" and the work was gone — a silent drop, which
         * is precisely what G10 exists to prevent, on a screen used in an archive with bad
         * signal.
         *
         * The KEY IS MINTED BEFORE THE ATTEMPT and reused by the queue: the dangerous case
         * is not "no signal" but a request that timed out AFTER the server acted, where a
         * fresh op would check the garment out twice. One id per act means the replay meets
         * its own receipt (migration 016).
         *
         * Auth failures are deliberately NOT queued — the person is standing here and can
         * sign in; "saved, will send later" when the truth is "sign in again" is the
         * reassuring lie this project refuses.
         */
        await photoQueue().enqueue({ kind: 'movement', payload: { itemId: id, direction: kind, note } }, input.idempotencyKey);
        queued += 1;
      } else refused.push(result.failure.message);
    }
    const verb = kind === 'out' ? 'checked out' : 'checked in';
    const parts = [`${String(done)} ${verb} ✓`];
    // Skipped is not a failure and not a success — it is the half of the selection this act
    // did not apply to, and saying so is what stops "0 checked in" reading as a bug.
    if (skipped > 0) parts.push(`${String(skipped)} already ${kind === 'out' ? 'out' : 'in'}`);
    if (queued > 0) parts.push(`${String(queued)} saved on this device — will send when you are back online`);
    if (refused.length > 0) parts.push(`${String(refused.length)} refused: ${refused[0] ?? ''}`);
    setBulkDone(parts.join(' — '));
    // The list's statuses have moved underneath us; re-read from the server rather than
    // patching rows in memory — the same rule the detail screen follows after a movement.
    void load(EMPTY_PAGE_STATE, appliedRef.current, filtersRef.current);
    setProjectionGen((g) => g + 1);
  };

  const actCtx = {
    newReservation: () => {
      setActsSheetOpen(false);
      setBookingFormOpen(true);
    },
    bulkAt: (at: 'booking' | 'outfit' | 'retire' | 'restore' | 'order' | undefined) => {
      setActsSheetOpen(false);
      setBulkOpenAt(at);
      setBulkOpen(true);
    },
    pickOutfit: () => {
      setActsSheetOpen(false);
      setOutfitPickerOpen(true);
      setOutfitChoices(null);
      void (async () => {
        const got = await call('listOutfits', () => api.listOutfits.query());
        setOutfitChoices(got.ok ? got.data.map((o) => ({ id: o.id, name: o.name })) : []);
      })();
    },
    exportSelected: () => {
      setActsSheetOpen(false);
      void exportSelected();
    },
    movement: (kind: 'out' | 'in') => {
      setActsSheetOpen(false);
      setMovementAsk(kind);
    },
    deleteSelected: () => {
      setActsSheetOpen(false);
      void runBulkDelete();
    },
  };

  const addToExistingOutfit = async (outfitId: string, name: string): Promise<void> => {
    const ids = [...selected];
    setOutfitPickerOpen(false);
    const result = await call('addItemsToOutfit', () => api.addItemsToOutfit.mutate({ outfitId, itemIds: ids }));
    if (!result.ok) {
      setProblem(result.failure.message);
      return;
    }
    // Reports what actually happened, not what was asked: ON CONFLICT DO NOTHING means a
    // garment already in the kit is a silent no-op, and "4 added" when two were already
    // there is a small lie the next person has to discover.
    const { added, alreadyThere } = result.data;
    setBulkDone(
      alreadyThere === 0
        ? `${String(added)} added to “${name}” ✓`
        : `${String(added)} added to “${name}” ✓ — ${String(alreadyThere)} already there`,
    );
  };

  /** Act outcomes are a TOAST beside the float since 0.1.455 (components/toast.tsx); the setter
   *  keeps its name. `selectNote` below stays inline — it narrates work in progress. */
  const [bulkDone, setBulkDone] = useToast();

  // A dashboard card's deep link (/items?status=x) arrives as the initial filter —
  // the same state the filter sheet sets, so the chip renders and is removable like
  // any hand-picked filter. The component is keyed on the status (app.tsx), so a
  // different card mounts fresh rather than mutating a live screen.
  //
  // REMEMBERED ACROSS TAB SWITCHES AND RELOADS since 0.1.522 (display/use-stored-filters.ts) — on
  // the catalogue only. A box's or a reservation's own list, the outfit picker and the barcode
  // picker each open on their own errand and never read or write the catalogue's memory.
  const [filters, setFilters] = useStoredFilters<ActiveFilters>(
    'items',
    // An explicit `initialStatus` (a dashboard card) OUTRANKS a remembered filter: you asked
    // for "everything checked out", not for where you were last time.
    (raw) => (initialStatus !== undefined ? null : sanitizeItemFilters(raw)),
    () =>
      initialStatus !== undefined
        ? { fields: {}, status: initialStatus }
        : // THE BARCODE PICKER OPENS ON THE BACKLOG (maintainer ruling, 27 Sep 2026): only the
          // garments with no barcode yet — as an ordinary chip, one tap to remove.
          forBarcode !== undefined
          ? { fields: {}, withoutBarcode: true }
          : (restored?.filters ?? NO_FILTERS),
    scopeId === undefined && pickTarget === undefined && forBarcode === undefined,
  );
  const [filterOpen, setFilterOpen] = useState(false);
  /**
   * THE DESK DATASHEET (display-options P5, frame 5) — the UNSCOPED catalogue only: the
   * box/booking sublists and every picking mode keep the flat list, so their errands and
   * layouts are untouched. Rows come from `listItems({displayAll})` — the same WHERE as
   * the paged list by construction, capped server-side; `deskCapped` falls back to the
   * flat list honestly rather than grouping a partial catalogue.
   */
  // Unscoped and not picking, at BOTH widths since phone display P3: the phone list rides the
  // same projection as the desk sheet (maintainer, 2 Sep: full projection + paged fallback).
  // PICKING IS THE ITEMS TAB'S OWN LIST, IN EVERY MODE AND AT BOTH WIDTHS (0.1.528; maintainer,
  // 3 Oct 2026: "all the picker screens should also have their display function, so that i can
  // group and sort and filter those lists in the picker screens as well. So they are the same as
  // their original tab list"). 0.1.523 gave the desk reservation picker the table; every other pick
  // errand — and the phone — kept the flat list, which is why the desk "+ Existing item" picker from
  // an outfit was a headless ItemRow list. Now each is the tab's: the desk DisplayBar + sheet, the
  // phone grouped list + Display pill. Its display is READ from the tab's but never written back
  // (`applyDeskDisplay` / `applyPhoneDisplay`), its filters are not remembered (`useStoredFilters`'
  // persist flag), and Views / Print / the add doors are the tab's, not the errand's. Only the box and
  // reservation SUBLISTS keep the flat list — they are one parcel's contents, not the catalogue.
  const sheetEligible = boxId === undefined && bookingId === undefined;
  const [fieldDefs, setFieldDefs] = useState<ItemFieldDef[]>([]);
  /** The whole intake meta, for the filter picker's Category and field dimensions (0.1.456). */
  const [intakeMeta, setIntakeMeta] = useState<IntakeMeta | null>(null);
  /** THE FILTER PICKER (0.1.456) — the door on every list that has a Display control; the
   *  scoped flat lists keep the old sheet (`filterOpen`). */
  const [pickerOpen, setPickerOpen] = useState(false);
  /** The source list, read the first time the picker opens — the Source dimension's options. */
  const [sourcesList, setSourcesList] = useState<SourceRow[] | null>(null);
  /** Stored field-axis configs sanitize against an EMPTY def list until intakeMeta lands
   *  — a stored custom-field grouping falls back to the default for one paint; accepted. */
  const [deskDisplay, setDeskDisplay] = useState<StoredDisplay>(() => initialItemsDisplay([]));
  const applyDeskDisplay = (next: StoredDisplay): void => {
    setDeskDisplay(next);
    // A picker's regrouping is the errand's, not the Items tab's resting view (0.1.523).
    if (pickTarget === undefined) saveDisplayState('items', next);
  };
  /** THE PHONE LIST'S VIEW STATE (frames 56/57, phone display P3): its own key, its own cap. */
  const [phoneDisplay, setPhoneDisplay] = useState<StoredDisplay>(() => initialItemsDisplay([], 'phone'));
  const applyPhoneDisplay = (next: StoredDisplay): void => {
    setPhoneDisplay(next);
    // The phone twin of the desk rule above (0.1.528): a picker's regrouping is the errand's.
    if (pickTarget === undefined) saveDisplayState('items.phone', next);
  };
  /** THE PHONE DISPLAY SHEET (frame 57) — open or not. */
  const [displayOpen, setDisplayOpen] = useState(false);
  /** The phone grouped list's root — the selection hook's scroll element there. */
  const phoneListRef = useRef<HTMLDivElement | null>(null);
  const [deskRows, setDeskRows] = useState<DisplayItemRow[] | null>(null);
  /**
   * THE PROJECTION'S RE-READ COUNTER (0.1.523). The grouped phone list and the desk sheet draw from
   * `deskRows`, which the effect below fetched only when the search or filters changed — while every
   * post-write re-read (`refresh()`, a bulk delete, a check-in, a box's Keep/Returned) reloaded only
   * the PAGED rows, which neither of those views shows. Field report, 1 Oct 2026: "when I delete an
   * item, it remains shown in the list until I switch tabs" — the delete had landed; the rows on
   * screen were the projection from before it. Every re-read now bumps this, and the effect lists it.
   */
  const [projectionGen, setProjectionGen] = useState(0);
  /** The desk camera button's hidden file input (the drop tile's click path, re-homed). */
  const photoPickRef = useRef<HTMLInputElement>(null);
  const [deskCapped, setDeskCapped] = useState(false);
  /** The projection read FAILED (offline, a 5xx): the flat list is the honest fallback — it
   *  carries the cached first page and the saved-copy band the sheet cannot (0.1.455). */
  const [deskFailed, setDeskFailed] = useState(false);
  useEffect(() => {
    if (!sheetEligible) return;
    void (async () => {
      const meta = await readIntakeMeta();
      if (meta.ok) {
        setIntakeMeta(meta.data);
        // EVERY kind (0.1.504) — the select-only filter here is why "I can't sort on colour" was
        // true for a text field. The spec decides per kind how a field groups, sorts and shows.
        const defs = meta.data.fields.map((f) => ({ key: f.key, label: f.label, kind: f.kind }));
        setFieldDefs(defs);
        // RE-SEED from storage against the FULL spec: the mount-time initializer ran with
        // no defs, so a stored custom-field grouping sanitized away — without this, every
        // detail round-trip silently dropped a field-axis view (caught by the P5 spec).
        setDeskDisplay(initialItemsDisplay(defs));
        setPhoneDisplay(initialItemsDisplay(defs, 'phone'));
      }
    })();
  }, [sheetEligible]);
  useEffect(() => {
    if (!sheetEligible) return;
    void (async () => {
      const r = await call('listItems', () =>
        api.listItems.query({
          ...(applied === '' ? {} : { search: applied }),
          ...filterDims(filters),
          // THE COLUMNAR SHAPE (0.1.478): the same rows as columns, ~2.4× less JSON for the
          // server to build and gzip and for this phone to parse; decoded once, right here, so
          // nothing below this line sees anything but the rows it always saw.
          displayAll: 'columns',
        }),
      );
      if (r.ok) {
        // One shape on the wire since 0.1.486: the columns, decoded here and nowhere else.
        setDeskRows(r.data.displayColumns !== undefined ? decodeDisplayColumns(r.data.displayColumns) : []);
        setDeskCapped(r.data.displayCapped === true);
        setDeskFailed(false);
      } else {
        setDeskFailed(true);
      }
    })();
  }, [sheetEligible, applied, filters, projectionGen]);
  /**
   * THUMBNAILS ARE SIGNED FOR THE ROWS ON SCREEN, NOT FOR THE CATALOGUE (0.1.473). The history
   * is two releases long: the projection's thumbs were signed for EVERY row, first one chunk of
   * 100 after another (16 sequential round trips at 1,940 items), then all chunks in one tick
   * (0.1.471). The maintainer's own Network panel (6 Sep 2026) showed what that does on the real
   * catalogue — ~4,200 items, ~47 requests of 100 keys fired at once, 4,700 presigns queuing on
   * one shared CPU at 0.4–2.7 s each, and the pictures landing ~10 s after the tap. A screenful
   * is a few dozen rows. So: ONE IntersectionObserver over every `[data-row-index]` row in the
   * sheet or the phone list, one screen of margin each way; a row that comes into view names its
   * key through the drawn order (`deskOrderRef`, the same index the selection gestures use);
   * keys are gathered per frame, deduped against known, in-flight and failed, and signed in one
   * request. Re-scanned whenever the rows, the view or the folds change, because rows mount and
   * unmount with the rails. The effect lives BELOW `deskOrder` (see there) so the ref is fresh.
   * A failure stays deliberately quiet, as the flat list's signer below explains: placeholders,
   * not an alert per screenful.
   */
  const deskOrderRef = useRef<DisplayItemRow[]>([]);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const thumbUrlsRef = useRef(thumbUrls);
  thumbUrlsRef.current = thumbUrls;
  const failedThumbsRef = useRef(failedThumbs);
  failedThumbsRef.current = failedThumbs;
  const inFlightThumbs = useRef(new Set<string>());
  /**
   * ID → NAME for the source chips (0.1.395). Read ONLY while a source filter is on: the chips are
   * the only thing on this screen that needs it, and the item list itself carries no source data —
   * so a catalogue nobody has filtered pays nothing for this. `[]` on a failure, which the chip
   * renders as "a deleted source" rather than a raw id.
   */
  const [sourceNames, setSourceNames] = useState<Map<string, string>>(new Map());
  const sourceFilterOn = (filters.sources ?? []).length > 0;
  // …and the same read feeds the picker's Source dimension the first time it opens (0.1.456).
  useEffect(() => {
    if (!(sourceFilterOn || pickerOpen) || sourcesList !== null) return;
    void (async () => {
      const result = await call('listSources', () => api.listSources.query());
      if (result.ok) {
        setSourcesList(result.data);
        setSourceNames(new Map(result.data.map((s) => [s.id, s.name])));
      }
    })();
  }, [sourceFilterOn, pickerOpen, sourcesList]);
  // The sort axis (redesign 1a "Sort: name"). `load` reads it through `sortRef` so the seven
  // existing call sites keep their signature; changing it resets pagination (the effect below).
  const [sort, setSort] = useState<ItemSort>('name');
  /** A→Z by default — the order the list has always opened in, so nobody's view moves on upgrade. */
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  /**
   * `Esc` CLEARS THE SELECTION (redesign, "Clear · Esc" — the bar labels the key so the
   * shortcut is discoverable rather than folklore). Scoped deliberately: only while a
   * selection exists, and never while a sheet or dialog is up — those own Esc for
   * themselves, and stealing it here would close a selection behind someone's back while
   * they meant to close a form.
   */
  useEffect(() => {
    if (noneSelected) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      if (actsSheetOpen || bulkOpen || filterOpen || bookingFormOpen || outfitPickerOpen) return;
      setSelected(new Set());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [noneSelected, actsSheetOpen, bulkOpen, filterOpen, bookingFormOpen, outfitPickerOpen]);

  const [selectNote, setSelectNote] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  /** Bumped on every new search; stale responses check it and drop themselves. */
  const generation = useRef(0);
  const sentinel = useRef<HTMLLIElement | null>(null);
  /** Prevents the observer double-firing a load while one is already in flight. */
  const inFlight = useRef(false);

  useEffect(() => {
    const handle = setTimeout(() => {
      setApplied(query.trim());
    }, 250);
    return () => {
      clearTimeout(handle);
    };
  }, [query]);

  /**
   * Sign a page's worth of thumb keys. A FAILURE HERE IS DELIBERATELY QUIET IN THE UI:
   * the list is fully usable text-first (that is a designed state, not a degraded one),
   * so a photo-signing outage renders placeholders rather than an alert per scroll page.
   * It is NOT silent — `call()` writes the ledger — the judgement is only that a toast
   * would tell the user nothing actionable while they are trying to find a garment.
   */
  const fetchThumbUrls = useCallback(async (keys: (string | null)[]) => {
    const wanted = [...new Set(keys.filter((k): k is string => k !== null))];
    if (wanted.length === 0) return;
    const result = await call('photoUrls', () => api.photoUrls.query({ keys: wanted }));
    if (result.ok) {
      setThumbUrls((known) => ({ ...result.data.urls, ...known }));
    }
  }, []);

  const load = useCallback(
    async (from: PageState, search: string, active: ActiveFilters) => {
      if (inFlight.current) return;
      inFlight.current = true;
      const gen = generation.current;
      setPhase('loading');
      const query = () =>
        api.listItems.query({
          ...(search === '' ? {} : { search }),
          // The box scope is not a user filter: it never appears as a chip and cannot be
          // cleared, because leaving it off would silently widen a box's list to the whole
          // catalogue.
          ...(boxId !== undefined ? { boxId } : bookingId !== undefined ? { bookingId } : {}),
          // Every filter dimension, from the one source (see `filterDims`). It absorbed the
          // three drift-prone copies that once let `withoutPhoto` show a chip and change nothing.
          ...filterDims(active),
          // Read from the ref so `load`'s signature (and its seven call sites) stay put.
          // `name` is the default, so it is left off the wire.
          ...(sortRef.current !== 'name' ? { sort: sortRef.current } : {}),
          // Sent only when it is NOT the axis's own default, so an unchanged view puts exactly the
          // same request on the wire as before 0.1.394 — and the server's per-sort default answers.
          ...(sortDirIsDefault(sortRef.current, dirRef.current) ? {} : { direction: dirRef.current }),
          limit: PAGE_SIZE,
          cursor: from.cursor,
        });

      /**
       * CACHED — but ONLY the unfiltered first page (offline plan §2.2 C, increment 4).
       *
       * Not every list read: a search or a filter is a question about the whole
       * catalogue, and answering "no matches" from a first-page cache would be a
       * confident wrong answer to a question the device cannot answer. The unfiltered
       * first page is the one §2.2 C names, and it is the one somebody in a stockroom
       * opens the app to see.
       */
      const cacheable =
        // ⚠️ A SCOPED LIST MUST NEVER BE CACHED (box OR booking). `cacheKey.itemListFirstPage` is one
        // global slot for the whole app, so caching a box's/booking's first page under it would
        // serve that scope's items as the ITEM TAB's offline list — a confident, wrong,
        // and persistent answer. The scope belongs with the other filters that already
        // disqualify the cache, for exactly the reason stated above them.
        scopeId === undefined &&
        search === '' &&
        from.cursor === null &&
        Object.keys(active.fields).length === 0 &&
        !active.category &&
        (active.status === undefined || active.status.length === 0) &&
        /**
         * ⚠️ `withoutPhoto` WAS MISSING FROM THIS LIST UNTIL 0.1.395, AND THAT WAS A CACHE LIE.
         * Every other dimension is here because a filtered first page written into the one global
         * slot is served to the next cold open as though it were the catalogue. The no-photo
         * filter is no different: leave it on, reload offline, and the app confidently shows the
         * unphotographed items as the whole item list. Found while adding the source filters
         * below — which is exactly why this list is written out rather than derived from
         * `countFilters`: a new dimension must be added HERE deliberately, and the omission is
         * visible in review only if the check is spelled out.
         */
        active.withoutPhoto !== true &&
        // 0.1.515 — "Barcode: none" is a filter like any other, and the barcode picker starts on
        // it: a first page cached under it would be the backlog served as the whole catalogue.
        active.withoutBarcode !== true &&
        (active.sources === undefined || active.sources.length === 0) &&
        (active.sourceKinds === undefined || active.sourceKinds.length === 0) &&
        // Only the DEFAULT order is the "what a stockroom opens the app to see" page; a
        // re-sorted first page under the one global cache slot would serve the wrong order.
        sortRef.current === 'name' &&
        // ⚠️ THE CACHE SLOT HOLDS THE DEFAULT ORDER ONLY. A reversed first page written into the
        // one global slot would be served to the next cold open as though it were the default —
        // the same reason the sort axis is checked on the line above.
        sortDirIsDefault('name', dirRef.current);

      // Staleness is carried OUT OF BAND rather than threaded through the result union:
      // the two branches then normalise to one `call()`-shaped value and every reader
      // below stays untouched.
      let cachedAt: number | null = null;
      const result = await (async () => {
        if (!cacheable) return call('listItems', query);
        const outcome = await cachedRead(readCache, cacheKey.itemListFirstPage, () => call('listItems', query));
        if (outcome.ok) {
          cachedAt = outcome.staleSince ?? null;
          return { ok: true as const, data: outcome.data };
        }
        return { ok: false as const, failure: outcome.failure };
      })();
      inFlight.current = false;
      // A response from a superseded search renders nothing — not even its error.
      if (gen !== generation.current) return;
      if (result.ok) {
        setPage(appendPage(from, result.data));
        setPhase('idle');
        setProblem(null);
        // `staleSince` is present ONLY on a cache hit, so this cannot mislabel live data.
        setStaleSince(cachedAt);
        // When this copy is live-but-later-offline, staleSince is null; this is the time the
        // band then reports. An instant, formatted by formatInstant — not a calendar date.
        setLastLoadedAt(Date.now());
        void fetchThumbUrls(result.data.items.map((i) => i.photoKey));
      } else {
        // Rows already shown stay shown; the failure is announced, not silently blank.
        setPhase('error');
        setProblem(result.failure.message);
      }
    },
    [],
  );

  // New search, new filter, OR new sort: bump the generation, reset, load the first page.
  // `sort` belongs here because the cursor is minted for one order — reusing rows fetched
  // under the old sort would interleave two orderings on screen.
  /**
   * ⚠️ THE PAGED FIRST PAGE IS STILL FETCHED ON THE DESK, though the sheet never shows it
   * (0.1.455, after a red gate). The first cut skipped it as "fetched anyway and never read"
   * — and ten specs went red: that page is the OFFLINE COPY (`cachedRead`, increment 4) and
   * the fallback the sheet drops to when the projection read fails, so skipping it left an
   * offline desk on "Loading the catalogue…" forever and lost the saved-copy band. A page of
   * fifty is ~135 ms in parallel with the projection; the offline path is worth it.
   */
  useEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    setPage(EMPTY_PAGE_STATE);
    void load(EMPTY_PAGE_STATE, applied, filters);
    // `load` is stable (useCallback with no deps); `applied`/`filters`/`sort` are the triggers.
  }, [applied, filters, sort, sortDir, load]);

  // OFFLINE, EAGERLY (redesign 1c). The band and the per-row pills exist for the person in a
  // basement, so they must appear when the network drops — not only after a read falls back
  // to cache. navigator.onLine plus the online/offline events is exactly what Playwright's
  // context.setOffline drives, so the e2e can prove it.
  useEffect(() => {
    const goOnline = (): void => setIsOffline(false);
    const goOffline = (): void => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // The queue is the source of truth for what is waiting to send; this subscription is the
  // nudge that keeps the band's count and the row pills honest as ops enqueue and drain.
  useEffect(
    () =>
      photoQueue().subscribe(() => {
        bumpQueueTick((n) => n + 1);
      }),
    [],
  );

  // Infinite scroll via sentinel. The Load-more button below is not a fallback for old
  // browsers — it is the path that works when the observer misfires, when the viewport
  // maths go wrong, and for keyboard/screen-reader users. Both call the same load.
  useEffect(() => {
    const node = sentinel.current;
    if (!node || page.exhausted || phase !== 'idle') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void load(page, applied, filters);
      },
      // ROOT IS THE SCROLLER, not the viewport — at a desk that is the list itself. With the
      // default root the sentinel inside an internally-scrolled list reports intersection
      // against a page that never moves, which is how one scroll became several loads.
      { root: listScrolls(listRef.current) ? listRef.current : null, rootMargin: '200px' },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [page, applied, filters, phase, load]);

  /**
   * Put the scroll back once the rows are actually on screen — a `scrollTo` before that
   * would land on a page with nothing to scroll. Fires once: `restoredScroll` latches, so
   * scrolling away and loading another page cannot yank you back.
   */
  useEffect(() => {
    if (restored === null || restoredScroll.current) return;
    if (page.rows.length === 0 || phase !== 'idle') return;
    restoredScroll.current = true;
    scrollListTo(scrollHost(), restored.scrollY);
  }, [restored, page.rows.length, phase]);

  /**
   * THE DATASHEET'S OWN VISUAL ORDER feeds the gesture hook while the sheet is showing —
   * shift-click ranges and drag select follow what is on screen, exactly as the flat
   * list's order does otherwise. The projection rows satisfy the hook's `{id}` contract.
   */
  const sheetShowing = sheetEligible && !deskCapped && !deskFailed && deskRows !== null;
  /**
   * THE DESK SHEET IS SHOWING FROM THE FIRST PAINT (0.1.455) — its head, its Display bar, and a
   * loading row where the rows will be — not only once the projection lands. Until 0.1.455 the
   * desk painted the PHONE layout (search row, Filter chip, the dashed tiles) for the whole
   * load and then swapped to the sheet: the maintainer read it as "shows me the old version"
   * for five seconds. Every desk-only head gate reads this, exactly as before P3.
   */
  const deskSheetPending = sheetEligible && !phone && !deskCapped && !deskFailed && deskRows === null;
  const deskSheetShowing = (sheetShowing || deskSheetPending) && !phone;
  /** The phone grouped list (frame 56). Over the cap the phone falls back to the flat paged
   *  list WITH its Filter chip — the desk's own rule; no dead pill. */
  /**
   * THE FILTER CHIP, one JSX value in two homes (0.1.498; maintainer, 9 Sep 2026): on the Items
   * tab and on a phone it stays in the search row, right of the search; inside a box on the DESK
   * it moves up into the acts row, to the LEFT of Select all — "the filter button on the desktop
   * version should go to the left of the select all button".
   */
  const filterChip = (
    <button
      type="button"
      className={countFilters(filters) > 0 ? 'chip chip-on' : 'chip'}
      onClick={() => setFilterOpen(true)}
    >
      Filter{countFilters(filters) > 0 ? ` (${String(countFilters(filters))})` : ''}
    </button>
  );
  const phoneListShowing = sheetShowing && phone;
  /**
   * THE PHONE TWIN OF `deskSheetPending` (0.1.523). The desk stopped painting the old layout while
   * its projection loaded in 0.1.455; the phone never got the same gate, so for the length of the
   * read the Items tab drew the PAGED fallback — a Filter pill beside the search, no Display pill,
   * the flat rows — and then swapped. Field report, 1 Oct 2026: "when I tap the item tab on mobile,
   * it briefly shows this wrong screen where the filter button shows next to the search and no
   * display button in the header". While pending, the band is the grouped list's and the rows are
   * one loading line; over the cap or on a failed read it falls back exactly as before.
   */
  const phoneListPending = sheetEligible && phone && !deskCapped && !deskFailed && deskRows === null;
  const phoneListLayout = phoneListShowing || phoneListPending;
  /** PRINT (P8): the sheet renders with every group open while the dialog is up. */
  const { printing, print } = usePrint();
  /** SAVED VIEWS (P7): the bar's Views menu, the save sheet, and the reads behind them.
   *  Keyed on the SHEET showing, not just the desk: scoped/picking lists never read views. */
  const savedViews = useSavedViews({
    screen: 'items',
    spec: itemsDisplaySpec(fieldDefs),
    fallback: ITEMS_DISPLAY_DEFAULT,
    config: deskDisplay.config,
    enabled: deskSheetShowing && pickTarget === undefined,
    onApply: (config) => applyDeskDisplay({ ...deskDisplay, config }),
    notify: setBulkDone,
  });
  // ONE tree at either width — the desk's config and cap, or the phone's.
  const deskTree = sheetShowing
    ? buildTree(
        deskRows,
        itemsDisplaySpec(fieldDefs),
        phone ? phoneDisplay.config : deskDisplay.config,
        phone ? PHONE_MAX_LEVELS : undefined,
      )
    : null;
  const deskOrder = deskTree !== null ? flattenRows(deskTree) : [];
  const deskIndex = new Map(deskOrder.map((r, i) => [r.id, i]));
  rowsRef.current = sheetShowing ? (deskOrder as unknown as ItemSummary[]) : page.rows;
  deskOrderRef.current = deskOrder;
  // The on-screen thumbnail signer — the note above `deskOrderRef` says why it is here and why
  // it keys on the tree's INPUTS rather than the tree (which is rebuilt every render).
  const foldedKey = phone ? phoneDisplay.collapsed.join(' ') : deskDisplay.collapsed.join(' ');
  const viewConfig = phone ? phoneDisplay.config : deskDisplay.config;
  useEffect(() => {
    if (!sheetShowing) return;
    const container = phone ? phoneListRef.current : sheetRef.current;
    if (container === null) return;
    let pending = new Set<string>();
    let frame = 0;
    const flush = (): void => {
      frame = 0;
      const keys = [...pending];
      pending = new Set();
      for (let i = 0; i < keys.length; i += 100) {
        const chunk = keys.slice(i, i + 100);
        for (const k of chunk) inFlightThumbs.current.add(k);
        void call('photoUrls', () => api.photoUrls.query({ keys: chunk })).then((result) => {
          for (const k of chunk) inFlightThumbs.current.delete(k);
          if (result.ok) setThumbUrls((known) => ({ ...result.data.urls, ...known }));
        });
      }
    };
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = Number((entry.target as HTMLElement).dataset['rowIndex']);
          const key = deskOrderRef.current[index]?.photoKey ?? null;
          if (
            key !== null &&
            thumbUrlsRef.current[key] === undefined &&
            !inFlightThumbs.current.has(key) &&
            !failedThumbsRef.current.has(key)
          ) {
            pending.add(key);
          }
        }
        if (pending.size > 0 && frame === 0) frame = requestAnimationFrame(flush);
      },
      // The viewport as root at both widths: intersection is clipped by every scroll container
      // on the way, so an internally-scrolled desk list reports honestly too. One screen ahead
      // and behind, so a flick does not land on a band of grey squares.
      { rootMargin: '100% 0px' },
    );
    container.querySelectorAll('[data-row-index]').forEach((row) => observer.observe(row));
    // Rows also arrive LATER, when a windowed band materialises on scroll (0.1.474) — watch the
    // container for them, so a band that comes into view gets its thumbnails without a re-scan.
    const arrivals = new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('[data-row-index]')) observer.observe(node);
          node.querySelectorAll('[data-row-index]').forEach((row) => observer.observe(row));
        });
      }
    });
    arrivals.observe(container, { childList: true, subtree: true });
    return () => {
      arrivals.disconnect();
      observer.disconnect();
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, [sheetShowing, phone, deskRows, fieldDefs, viewConfig, foldedKey, printing]);
  selectingRef.current = selecting;
  // Same reason as `selectedRef` above: `onRowClick` is `useCallback(..., [])` so the row
  // memo holds, and a dependency-free callback cannot close over these.
  queryRef.current = query;
  appliedRef.current = applied;
  filtersRef.current = filters;
  sortRef.current = sort;
  dirRef.current = sortDir;
  boxIdRef.current = boxId;
  bookingIdRef.current = bookingId;
  scopedRef.current = scopeId;

  // The sheet's empty states read the PROJECTION (0.1.455) — the paged rows are not what it
  // shows, and "Nothing matches" must appear on an empty sheet exactly as on an empty list.
  const empty = sheetShowing && deskRows !== null ? deskRows.length === 0 : page.rows.length === 0 && phase === 'idle';

  /** Nothing selected = nothing to protect, so the arm is dropped rather than left to
   *  surprise the next single click. */
  useEffect(() => {
    if (selected.size === 0 && armedRow.current !== null) {
      armedRow.current = null;
      setArmed(null);
    }
  }, [selected]);

  /** The row's tap: toggle in selection mode, navigate otherwise. Reads `selecting` from a
   * ref so it can stay dependency-free (see the refs above). */
  const onRowClick = useCallback((row: ItemSummary): void => {
    if (pick.swallowClick()) return; // the click that trails a finished drag gesture
    /**
     * ⚠️ WHILE A SELECTION EXISTS, OPENING TAKES TWO CLICKS (field report, 5 Aug 2026:
     * "to prevent accidental click while selecting items"). Mid-selection the hand is on
     * the rows, and one stray tap used to throw away the selection by navigating away from
     * it — the selection is not persisted, so that is destructive in the only sense that
     * matters to somebody forty rows into building one.
     *
     * NO TIMER, deliberately. A double-click window would make the fix depend on how fast
     * somebody clicks, and the request is literally "click an item twice". The first click
     * ARMS the row and the second opens it, however long the gap. Clicking a different row
     * re-arms rather than opening, so a wandering finger never navigates.
     *
     * With NOTHING selected the row opens on one click exactly as before — the guard only
     * exists to protect a selection, so with no selection it must not be felt.
     */
    if (selectedRef.current.size > 0 && armedRow.current !== row.id) {
      armedRow.current = row.id;
      setArmed(row.id);
      return;
    }
    armedRow.current = null;
    setArmed(null);
    /**
     * THE ROW OPENS THE ITEM; THE CHECKBOX SELECTS. With select mode permanent these had to
     * stop being the same tap — "I now need to still be able to click into an item detail
     * screen by clicking on its title or thumbnail". The checkbox keeps the drag and
     * shift-click range gestures; everything else on the row navigates.
     */
    {
      // Saved on the way OUT, not restored on the way in: the list knows its own search,
      // filters and scroll here, and the detail screen would have to be told them.
      // Same global-slot reasoning as the cache: a position saved from inside a box would
      // be consumed by the Items tab on its next visit, restoring a search the user never
      // typed there.
      if (scopedRef.current === undefined) {
        rememberListPosition({
          query: queryRef.current,
          applied: appliedRef.current,
          filters: filtersRef.current,
          scrollY: listScrollTop(scrollHost()),
        });
      }
      /**
       * CARRIES THE BOX WHEN THIS LIST IS SCOPED TO ONE (0.1.167). `Items` IS the box's item
       * list since 0.1.166, so this one call serves both tabs — and the detail's back button
       * must return to the box rather than to the global item list, which is the same
       * complaint the outfits screen had. `boxIdRef` exists because this callback is
       * `useCallback(…, [])` and cannot close over the prop.
       */
      // ⚠️ AND THE RESERVATION (0.1.524, maintainer 2 Oct: "when I'm in a reservation screen and I
      // click on an item … the back button then takes me to the item list"). Only `boxId` was read
      // here, so a garment opened inside a reservation carried no origin. The chip now pops history
      // (`goBack`) and does not need this; the origin is what a RELOAD of the item falls back to.
      const box = boxIdRef.current;
      const booking = bookingIdRef.current;
      navigate(
        box !== undefined
          ? { name: 'item', id: row.id, from: { kind: 'box', id: box } }
          : booking !== undefined
            ? { name: 'item', id: row.id, from: { kind: 'booking', id: booking } }
            : { name: 'item', id: row.id },
      );
    }
    // `[]`, not `[toggleRow]`: the body stopped calling it when the row stopped toggling,
    // and the comment above `selectedRef` already describes this callback as `useCallback(…, [])`.
    // The stale dependency contradicted its own documentation and cost the memo a re-render.
  }, []);

  const onThumbError = useCallback((photoKey: string): void => {
    setFailedThumbs((prev) => new Set(prev).add(photoKey));
  }, []);

  /** Export exactly the picked items (maintainer request, 27 Jul). The FULL export
   * lives in Settings — this one is the subset, and the server's filename says so. */
  const exportSelected = async (): Promise<void> => {
    setExporting(true);
    setSelectNote('Preparing the export…');
    const outcome = await downloadExport([...selected]);
    setSelectNote(outcome.message);
    setExporting(false);
  };

  /** Select EVERY row matching the current search + filters — keyset-paged to the end
   * (the maintainer's cleanup passes span hundreds of items; stopping at one page was
   * the old, overruled behavior), replacing the selection and saying what it did. The
   * sanity bound only exists to stop a runaway payload, and reaching it is REPORTED. */
  /**
   * THE FILTER SHEET'S LIVE COUNT (frame 3c). Given a DRAFT of the filters, returns how many
   * garments applying it would show — the sheet's "N match / Show N". It is the list's OWN query
   * (same `filterDims`, same box scope, same applied search), asked with `limit: 1` and read for
   * `total`, so the number cannot disagree with what applying actually produces. `total` is a
   * no-cursor COUNT over the same conditions (items.ts §THE MATCHING TOTAL); `limit: 1` keeps the
   * row payload to nothing while every debounced keystroke re-asks it. Returns null on failure so
   * the sheet holds its last known number rather than flashing to zero.
   */
  const countFor = useCallback(
    async (draft: ActiveFilters): Promise<number | null> => {
      const result = await call('listItems', () =>
        api.listItems.query({
          ...(applied === '' ? {} : { search: applied }),
          ...(boxId !== undefined ? { boxId } : bookingId !== undefined ? { bookingId } : {}),
          ...filterDims(draft),
          limit: 1,
        }),
      );
      return result.ok ? (result.data.total ?? 0) : null;
    },
    [applied, boxId],
  );

  const selectAllMatching = async (): Promise<void> => {
    /**
     * ONE REQUEST, NOT TWENTY.
     *
     * ⚠️ MEASURED BEFORE IT WAS CHANGED (maintainer, 31 Jul 2026: "the mass select has
     * become slower again" — and "measure it first"). Against 1,903 items in a real
     * browser: 956 ms with fifty rows on screen, 2,299 ms with the whole list loaded.
     *
     * The cost was not rendering. This loop asked for 100 ids at a time because that is
     * `MAX_LIMIT` on the list read, so nineteen hundred items meant twenty sequential
     * round trips — each one carrying the category join, the photo sub-select, the tag
     * array and the match rank, to collect ids and discard everything else.
     *
     * `idsOnly` answers with every matching id in a single query. It shares the list's own
     * filter construction rather than repeating it: a filter added to one and missed in the
     * other is how "Select all" would come to mean something different from what the list
     * shows — which already happened once, with `withoutPhoto`.
     *
     * For reference, the other two numbers from the same run, because they are the honest
     * limit of this fix: one toggle cost 41 ms with fifty rows rendered, 85 ms with three
     * hundred and 293 ms with all 1,903. That is reconciliation over however many rows are
     * on screen and it is linear in them; only rendering fewer rows fixes it, which is a
     * change of its own and is not this one.
     */
    setSelectNote('Selecting…');
    const result = await call('listItems', () =>
      api.listItems.query({
        ...(applied === '' ? {} : { search: applied }),
        ...(boxId !== undefined ? { boxId } : bookingId !== undefined ? { bookingId } : {}),
        ...filterDims(filters),
        idsOnly: true,
      }),
    );
    if (!result.ok) {
      // Announce and select NOTHING: a partial "all" silently missing the tail is exactly
      // the wrong-count bulk edit this feature must never produce.
      setSelectNote(result.failure.message);
      return;
    }

    /**
     * An older API has no `idsOnly` and answers with a page of ROWS and no `ids`. Selecting
     * that page and calling it "all" would be the wrong-count bulk edit wearing a
     * confirmation tick, so it refuses and says why. The web bundle reaches Cloudflare in
     * seconds and the API reaches Fly minutes later, so this window is real on every
     * release.
     */
    const ids = result.data.ids;
    if (ids === undefined) {
      setSelectNote('Select all needs the updated server. Try again in a minute — nothing was selected.');
      return;
    }

    const capped = ids.length >= MAX_BULK;
    setSelected(new Set(ids.slice(0, MAX_BULK)));
    pick.resetAnchor(); // a select-all leaves no meaningful anchor for a later shift-click
    setSelectNote(
      capped
        ? `Selected the first ${String(MAX_BULK)} matching items — the sanity cap; narrow the search to reach the rest.`
        : `Selected all ${String(ids.length)} matching item${ids.length === 1 ? '' : 's'}.`,
    );
  };

  /** Fresh first page after a bulk apply — the list re-reads the server rather than
   * patching rows it merely believes changed (post-write verification, list edition). */
  const refresh = (): void => {
    generation.current += 1;
    inFlight.current = false;
    setPage(EMPTY_PAGE_STATE);
    void load(EMPTY_PAGE_STATE, applied, filters);
    setProjectionGen((g) => g + 1);
  };

  /**
   * CATALOGUE PHOTOS → NEW ITEMS. Mirrors the box detail's attach (boxes.tsx): wait for the whole
   * strip to settle, then one `addPhotoItems` for the batch (not one per photo), remove only what
   * was attached, and re-read the list. A FAILED photo stays in the strip with its Retry — no
   * silent drop (G10); `call()` also writes the ledger. */
  const attachReady = photos.filter((p) => p.status === 'done' && p.key !== undefined);
  const photosInFlight = photos.some(
    (p) => p.status === 'compressing' || p.status === 'uploading' || p.status === 'queued',
  );
  const addPhotoItems = async (): Promise<void> => {
    if (attachReady.length === 0 || attachingItems) return;
    setAttachingItems(true);
    const batch = attachReady;
    const result = await call('addPhotoItems', () =>
      api.addPhotoItems.mutate({ objectKeys: batch.map((p) => p.key!) }),
    );
    setAttachingItems(false);
    if (result.ok) {
      for (const photo of batch) removePhoto(photo.id);
      refresh();
    } else setBulkDone(result.failure.message);
  };
  const readyKeys = attachReady.map((p) => p.key!).join(',');
  useEffect(() => {
    // One attach when the strip settles — keyed on the finished uploads so it fires once per set.
    if (!catalogueAdd || attachReady.length === 0 || attachingItems || photosInFlight) return;
    void addPhotoItems();
    // Keyed on the finished uploads (not `photos`) so it fires once per settled set, like the box.
  }, [readyKeys, photosInFlight]);

  /**
   * BULK DELETE (0.1.345) — a client loop over the single `deleteItem`, the exact shape the boxes
   * multi-select uses (`boxes.tsx` bulkDelete). Sequential (the pool rule), one confirm naming the
   * count, and it reports what actually happened. Any refusal is still counted and shown rather
   * than dropped (G10).
   *
   * ⚠️ SINCE 071 A GARMENT NO LONGER REFUSES because it has history — the maintainer's 25 Aug
   * ruling made deletion unconditional, and the history is deleted with it. The refusal path stays
   * (RLS can still say no, and an un-migrated database says "needs migration 071"), but the confirm
   * copy no longer promises that items with a past will be kept.
   */
  const runBulkDelete = async (): Promise<void> => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const ok = await ask({
      title: `Delete ${String(ids.length)} item${ids.length === 1 ? '' : 's'}?`,
      body: `The garments are DELETED — not retired, not archived, gone — and so is their history: every check-out, return and edit. There is no undo.`,
      confirmLabel: ids.length === 1 ? 'Delete item' : `Delete ${String(ids.length)} items`,
    });
    if (!ok) return;
    let deleted = 0;
    const refused: string[] = [];
    for (const id of ids) {
      const r = await call('deleteItem', () => api.deleteItem.mutate({ id }));
      if (r.ok) deleted += 1;
      else refused.push(r.failure.message);
    }
    const parts = [`${String(deleted)} deleted ✓`];
    if (refused.length > 0) parts.push(`${String(refused.length)} kept: ${refused[0] ?? ''}`);
    setBulkDone(parts.join(' — '));
    setSelected(new Set());
    refresh();
  };

  /**
   * THE ACTIONS COLUMN'S STICKY OFFSET IS MEASURED, NOT ASSUMED (0.1.171).
   *
   * It was `calc(--app-header-h + --items-head-h)` — two constants. That is right on the item
   * list, where the sticky header really is 6.5rem tall, and WRONG on a box detail, where
   * `headExtra` puts a photo strip inside that same header and makes it far taller. The column
   * pinned ABOVE the header's real bottom edge and slid behind it while the list scrolled,
   * which is exactly what was reported: the column "scrolls with the item list".
   *
   * A second constant for the box case would have been a third number to keep in sync with a
   * header anyone can add a row to. Measuring costs one ResizeObserver and cannot drift.
   * `--items-head-h` stays as the FALLBACK, so nothing depends on this effect having run.
   */
  const rootRef = useRef<HTMLElement | null>(null);
  const headRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  /** Whichever list element is mounted — the phone grouped list, the desk sheet (a scroller
   *  since 0.1.487), or the flat list. The seam's helpers above ask it whether it scrolls. */
  const scrollHost = (): HTMLElement | null => phoneListRef.current ?? sheetRef.current ?? listRef.current;
  useEffect(() => {
    const head = headRef.current;
    const root = rootRef.current;
    const grid = gridRef.current;
    if (head === null || root === null) return undefined;
    const apply = (): void => {
      root.style.setProperty('--items-head-measured', `${String(head.getBoundingClientRect().height)}px`);
      /**
       * THESE TWO NUMBERS GIVE THE GRID THE REST OF THE VIEWPORT (0.1.180), which is what
       * stops the PAGE scrolling at a desk and therefore what holds the actions column still.
       *
       * The probe that settled it: a box with 8 items at 1280×900 had 189px of scroll while
       * the column's flow position sat 223px above its offset — sticky can never pin there,
       * because the page runs out of scroll first. `position: fixed` (0.1.178) held it still
       * and put it in the wrong place, floating over the box title. Neither primitive can keep
       * a column beside a list still while the page under it moves, so the page does not move.
       */
      if (grid !== null) {
        /**
         * WHERE THE GRID STARTS, so it can be given exactly the rest of the viewport.
         * `+ scrollY` converts the viewport-relative top to a document offset; once the
         * height below is applied the page stops scrolling, so this settles at its true
         * value on the first tick and stays there.
         */
        const rect = grid.getBoundingClientRect();
        const top = rect.top + window.scrollY;
        root.style.setProperty('--items-grid-top', `${String(top)}px`);
        /**
         * AND WHAT SITS BELOW IT — the CONTENT after the grid, plus the padding the page keeps
         * under it. 104px of it on a box detail, and subtracting a guessed constant left the
         * page scrolling by exactly the part that was guessed wrong (measured twice: 46px, then
         * 46px again with a bigger guess).
         *
         * ⚠️ NOT `documentElement.scrollHeight` ANY MORE, and the reason is the whole bug
         * (0.1.189). That read claimed to be invariant — "shrinking the grid moves the document
         * bottom up by the same amount it moves the grid bottom" — and it is, right up until
         * the page STOPS SCROLLING, which is the entire point of this layout. A document
         * shorter than the viewport reports `scrollHeight` clamped to the viewport, so it stops
         * tracking content: the grid shrinks, the reported bottom does not move, and the tail
         * silently becomes THE EMPTY GAP THE GRID JUST VACATED. It then feeds that gap back in
         * as a height to subtract, and settles there.
         *
         * Measured at 1280×900: tail 240.6px with 24px of real content below the grid, so the
         * list stopped 217px above the bottom of the screen. Reported as "the item list should
         * go down to the bottom of the screen". Self-consistent, stable, and wrong.
         *
         * So the trailing content is measured DIRECTLY. Out-of-flow siblings are skipped: a
         * filter sheet or an outfit picker is an overlay, and counting its height would shorten
         * the list by the size of a panel that is floating over it.
         */
        let contentBottom = rect.bottom;
        for (let sibling = grid.nextElementSibling; sibling !== null; sibling = sibling.nextElementSibling) {
          const style = getComputedStyle(sibling);
          if (style.position !== 'static' && style.position !== 'relative') continue;
          const siblingRect = sibling.getBoundingClientRect();
          // ⚠️ PLUS ITS BOTTOM MARGIN. `getBoundingClientRect` is the border box and excludes
          // margins, so the first version of this left the page scrolling by exactly the
          // trailing paragraph's 16px — the same shape of error as the guessed constants it
          // replaced, one element further down.
          if (siblingRect.height > 0) {
            contentBottom = Math.max(contentBottom, siblingRect.bottom + (parseFloat(style.marginBottom) || 0));
          }
        }
        /* Whatever padding the shell keeps below this screen — measured for the same reason
           everything else here is: `body` has none today and a future wrapper may. */
        let padding = 0;
        for (let node: HTMLElement | null = root; node !== null; node = node.parentElement) {
          padding += parseFloat(getComputedStyle(node).paddingBottom) || 0;
          if (node === document.body) break;
        }
        const tail = Math.max(0, contentBottom - rect.bottom + padding);

        /**
         * ⚠️ THE `data-acts-overflow` MEASUREMENT THAT STOOD HERE IS GONE WITH ITS SUBJECT
         * (0.1.213). It decided when the acts COLUMN did not fit beside the list and released
         * both height caps so the page could scroll — the 7 Aug ruling ("make the boxes
         * action column the exact same as the item list action column, i dont care if the
         * entire page has to scroll"). The redesign deletes the column at every width
         * (decision 3: the bulk bar in the header), so there is nothing left to fit, and the
         * no-page-scroll layout holds unconditionally again. If a side column ever returns,
         * git has the measurement AND the ruling — re-read both before re-deriving either.
         */
        // Written only when it MOVES, and never for a sub-pixel wobble: a style write is a
        // layout invalidation, and this used to run on every observation.
        const prev = root.style.getPropertyValue('--items-grid-tail');
        if (prev !== `${String(tail)}px` && Math.abs(parseFloat(prev || '0') - tail) > 0.5) {
          root.style.setProperty('--items-grid-tail', `${String(tail)}px`);
        }
      }
    };
    apply();
    window.addEventListener('resize', apply);
    if (typeof ResizeObserver === 'undefined')
      return () => {
        window.removeEventListener('resize', apply);
      };
    /**
     * ⚠️ THE HEAD ONLY — NEVER THE GRID. `apply` sets `--items-grid-top`/`--items-grid-tail`,
     * which change the grid's own height, so observing the grid makes the callback its own
     * trigger. Locally the loop settles in a frame or two and looks fine; on CI's slower CPU
     * it thrashed the main thread hard enough that the SERVICE WORKER never got to claim the
     * page, and `offline-shell.spec` failed on a 20s `serviceWorker.controller` wait — twice,
     * on a different test each time, which is what a starved main thread looks like rather
     * than a broken assertion.
     *
     * The head is safe to observe: nothing here changes its height. Window resize covers the
     * case the grid observation was reaching for.
     */
    const observer = new ResizeObserver(apply);
    observer.observe(head);
    return () => {
      window.removeEventListener('resize', apply);
      observer.disconnect();
    };
  }, []);

  /**
   * THE ACTIVE FILTERS AS CHIPS — ONE derivation (`describeItemFilters`), three homes since
   * 0.1.456: the desk Display bar beside the Sort chip, the desk panel's Filters section, and
   * the phone Display sheet's Filters row; on a flat list, under the head as before. The
   * whole chip removes its filter. `itemFilterDimensions` is what the picker offers.
   */
  const itemChips = describeItemFilters(filters, sourceNames).map((c) => ({
    ...c,
    onRemove: () => setFilters((f) => removeItemFilter(f, c.key)),
  }));
  /** The field values the loaded rows carry, per label — the picker's options for a field with no
   *  list of its own (0.1.505). A multi-select's cell is its values joined with ' · ' (the API's
   *  fields join), so it is split back here. */
  const presentFieldValues = useMemo(() => {
    const out: Record<string, Set<string>> = {};
    for (const r of deskRows ?? []) {
      for (const [label, v] of Object.entries(r.fields)) {
        if (v === '') continue;
        const bucket = (out[label] ??= new Set<string>());
        for (const part of v.split(' · ')) if (part !== '') bucket.add(part);
      }
    }
    return Object.fromEntries(Object.entries(out).map(([k, set]) => [k, [...set]]));
  }, [deskRows]);
  const pickerDims = itemFilterDimensions(intakeMeta, sourcesList, filters, setFilters, presentFieldValues);
  /** The list's door: the picker where a Display control exists, the old sheet on a scoped list. */
  const openFilterDoor = (): void => {
    if (sheetEligible) setPickerOpen(true);
    else setFilterOpen(true);
  };
  const filterChipsEl = itemChips.length > 0 ? <FilterChips chips={itemChips} onAdd={openFilterDoor} /> : null;

  /** THE BOOKING WEARS THE BOX'S CLOTHES (0.1.503; maintainer, 9 Sep 2026: "the items section should
   *  be exactly the same as the current build item section in the box detail screen"). Everything
   *  0.1.501 gave a box — the sheet rows and column head, the desk Filter chip in the acts row, the
   *  phone Select pill — is keyed on `scoped`, not on the box alone. The gates that build the QUERY
   *  stay on `boxId` / `bookingId` each: a box and a reservation are different reads. */
  const scoped = boxId !== undefined || bookingId !== undefined;

  return (
    // No `selection-bar-open` any more: that class reserved room at the bottom for the
    // fixed bar, and the bar is gone. Leaving it would be permanent dead space under the list.
    <section
      ref={rootRef}
      className={
        picking
          ? 'items-pick'
          : bookingId !== undefined
            ? 'items-in-box items-in-booking'
            : scopeId !== undefined
              ? 'items-in-box'
              : 'items-main'
      }
    >
      {/* THE PHONE'S BACK CLEARS THE TICKS (0.1.527; maintainer, 2 Oct 2026: "when I have items
          selected, make it so that my android back button clears my item selection, this should be
          app wide"). Mounted only while something is ticked — one guard for every surface this list
          is (tab, box, reservation, picker); Esc above is the desk's twin. */}
      {!noneSelected && <SelectionBackGuard onClear={() => setSelected(new Set())} />}
      {/**
        * ONE STICKY HEADER — maintainer, 30 Jul 2026: "make it so that the whole top part
        * of the item list sticks… the title Items, add item, select all button, search
        * box, filter button", and make those buttons "more compact and readable and
        * distinguishable from each other".
        *
        * It sticks BELOW the tab bar rather than at the top of the viewport, because the
        * bar is itself sticky at 0 and two things pinned to the same edge overlap. The
        * offset is `--nav-h`, which the nav is sized to exactly — a shared number beats
        * two independent guesses, and `mobile-layout.spec.ts` measures that they do not
        * overlap after a scroll.
        *
        * Search first in the DOM would be tidier, but the title anchors the screen for
        * anyone arriving from the tab bar, so it stays and simply takes less room.
        */}
      <div
        // The box-scoped reuse floats its primary too now (frame 5a): both surfaces get the
        // floating treatment on a phone, and the box's old fixed bottom bar is retired.
        className={`items-head${noneSelected ? '' : ' items-head-selecting'} items-head-floating`}
        ref={headRef}
      >
        {/**
          * THE BULK BAR — redesign decision 3. With a selection, the title row is REPLACED
          * IN PLACE by this bar at desk width: same height, so selecting costs zero vertical
          * space and the list never reflows under a drag (the constraint the old fixed
          * selection bar died for is inherited by whatever stands here — see below).
          *
          * ⚠️ RENDERED ALWAYS, SHOWN BY CSS. The phone keeps the title row (its door is the
          * Actions button + full-screen sheet); a conditional render would also unmount the
          * title mid-gesture at desk width, and the swap is a presentation fact, not a state
          * fact. (`.items-head .items-bulkbar` hides it; `.items-head-selecting` shows it.)
          *
          * ON THE SHARED `HeaderActs replace` SINCE 3 OCT 2026 (maintainer: "first show N amount
          * selected, clear selection button, then the delete and retire button, then a small
          * vertical break line, then the rest of the actions"; ruled: Items follows the same order
          * as the tabs). So: ✓ N selected · Clear selection · Esc · Delete N… · Retire N… (red) ·
          * divider · every other act inline. ⚠️ NO MORE ▾ ("all the actions should be displayed"):
          * its three — Add to an existing reservation, Add to an existing outfit, Restore — and a
          * box's own Keep / Returned / Remove from box (`extraBulkActions`, `tail`) are pills in the
          * row, which scrolls sideways when it does not fit. Restore stays unconditional, as it was
          * behind More ▾ (the phone sheet offers it the same way — select.spec's parity claim).
          *
          * THE FULL ROW FROM 701px (0.1.530; it was ≥1024 — 701–1023 showed the compact face, which
          * broke "all the actions should be displayed"). Below 701 the compact face stands (frame 1b):
          * count · Actions (n) · Clear, CSS hides the inline acts, `lead` is the door to the sheet.
          */}
        <HeaderActs
          replace
          className={reservationActs === true ? 'items-bulkbar items-bulkbar-none' : 'items-bulkbar'}
          countClassName="items-bulkbar-count"
          announce
          escHint={!compact}
          label="Actions for the selected items"
          count={selected.size}
          onClear={() => setSelected(new Set())}
          lead={
            /* THE PHONE-WIDTH DOOR TO THE ACTS (frame 1b): count · Actions (n) · Clear, and this opens
               the full-screen sheet. From 701 CSS hides it; the inline pills are the desk's acts. */
            <button type="button" className="btn-primary chip items-bulkbar-actions" onClick={() => setActsSheetOpen(true)}>
              Actions ({selected.size})
            </button>
          }
          acts={[
            { label: `Delete ${String(selected.size)}…`, tone: 'danger', onClick: () => actCtx.deleteSelected() },
            { label: `Retire ${String(selected.size)}…`, tone: 'danger', onClick: () => actCtx.bulkAt('retire') },
            /* AFTER THE DIVIDER (0.1.531; maintainer, 3 Oct 2026): "Add to an existing outfit. Then add
               to a new outfit. Then add to an existing reservation. Then add to a new reservation" —
               each pair existing (solid) then new (outlined) — then Edit fields and Restore, plain. */
            { label: 'Add to an existing outfit', tone: 'existing', onClick: () => actCtx.pickOutfit() },
            { label: 'Add to a new outfit', tone: 'new', onClick: () => actCtx.bulkAt('outfit') },
            { label: 'Add to an existing reservation', tone: 'existing', onClick: () => actCtx.bulkAt('booking') },
            { label: 'Add to a new reservation', tone: 'new', onClick: () => actCtx.newReservation() },
            { label: 'Edit fields', onClick: () => actCtx.bulkAt(undefined) },
            { label: 'Restore', onClick: () => actCtx.bulkAt('restore') },
          ]}
          tail={
            /* The box detail's Keep / Returned / Remove from box — the same slot the phone sheet
               renders, so the two surfaces stay in step. */
            extraBulkActions?.([...selected], (summary) => {
              setBulkDone(summary);
              void load(EMPTY_PAGE_STATE, appliedRef.current, filtersRef.current);
              setProjectionGen((g) => g + 1);
            })
          }
        />
        {/* PICKING IS ONE HEADER ROW (0.1.527; maintainer, 2 Oct 2026: "the back button has a whole
            row to itself, so move it down into the row below so that we don't have 2 rows in the
            header"). The band carries the back chip, the search, the Filter chip (flat list only —
            on the desk sheet the Display bar owns it), Select all, Clear and the act; the title row
            and the search row below are NOT rendered while picking, so the search is never doubled.
            The "Picking garments for this …" / "Tick the garment that carries barcode …" captions
            are gone: the act's label ("Add N to X", "Link barcode to this item") already says what
            ticking a row does. The phone keeps its floating act (`.float-primary`); the band's act
            is the desk's (CSS hides it under the phone line). 0.1.397's "band is its own row"
            split painted the search white with it — the band's children are now laid out by
            `.picker-band > .picker-band-search / .picker-band-act`, not by the head row. */}
        {/* THE PHONE PICKER (0.1.528; maintainer, 3 Oct 2026: "on mobile in the header we have the back
            button … and the display pill button to group, sort and filter. And below the header we
            have the search box. and then below the search box we have the list we pick from"). The
            band is back chip · Display pill; the search is its own row below (the tab's search row,
            `ListSearchRow`), Clear at its right; the act floats (`.float-primary`, below). Over the
            projection's cap or on a failed read the phone falls back to the flat list — no pill, and
            the Filter chip rides the search row instead, the tab's own fallback rule. */}
        {picking && phone && (
          <>
            <PickerBand
              backTo={pickBackTo}
              label={pickLabelName}
              trailing={
                phoneListLayout ? (
                  <DisplayPill spec={itemsDisplaySpec(fieldDefs)} config={phoneDisplay.config} onOpen={() => setDisplayOpen(true)} />
                ) : undefined
              }
            />
            <ListSearchRow
              value={query}
              onChange={setQuery}
              placeholder="Search name, brand, code…"
              label="Search items"
              trailing={
                <>
                  {!phoneListLayout && filterChip}
                  {!noneSelected && (
                    <button type="button" className="chip chip-clear" onClick={() => setSelected(new Set())}>
                      Clear selection
                    </button>
                  )}
                </>
              }
            />
          </>
        )}
        {picking && !phone && (
          <PickerBand backTo={pickBackTo} label={pickLabelName}>
            <button type="button" className="chip items-select-all" onClick={() => void selectAllMatching()}>
              Select all
            </button>
            {!noneSelected && (
              <button
                type="button"
                className="chip chip-clear items-cancel-selection"
                onClick={() => setSelected(new Set())}
              >
                Clear selection
              </button>
            )}
            {/* THE ONE ACT in pick-mode: file the ticked rows into the target, count in the
                label. Disabled until something is ticked; disarmed while the add is in flight. */}
            <button
              type="button"
              className="btn-primary chip picker-band-act"
              disabled={!pickReady || addingToPick}
              onClick={() => void addSelectedToPick()}
            >
              {addLabel}
            </button>
            {/* ACTS LEFT, SEARCH AT THE RIGHT END (0.1.528; maintainer, 3 Oct 2026: "In the desk item
                picker screen make sure that the actions are to the left of the search bar") — the
                tabs' own order (`HeaderActs` sits left of the band search). The Filter chip, when the
                desk sheet is not showing, stays the search's neighbour: it is a list tool, not an act. */}
            {!deskSheetShowing && filterChip}
            <input
              type="search"
              className="items-search picker-band-search band-search"
              placeholder="Search name, brand, code…"
              aria-label="Search items"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
            />
          </PickerBand>
        )}
        {!picking && (
        <div
          className={`items-head-row items-head-title${inBand === true ? ' items-head-title-banded' : ''}${
            reservationActs === true ? ' items-head-title-none' : ''
          }`}
        >
          {/* The muted garment count beside the title (redesign 1a) — the matching total,
              so a search narrows it. Held in `page.total`; absent until the first page
              lands, and never a guessed number. */}
          {inBand !== true && (
          <h2 className="items-head-h">
            {/* The pick-mode caption that lived here left with the title row (0.1.527 — above). */}
            <>
                {/* THE TITLE IS THE AREA CHIP ON THE MAIN TAB (10b), a plain "Items" when the list
                    is EMBEDDED in a box or reservation (scopeId set) — there the surrounding detail
                    screen owns the nav, and a switcher inside its item list would be a second, wrong
                    one. The waiting badge follows the same rule for the same reason. */}
                {scopeId === undefined ? <AreaSwitch area="items" /> : 'Items'}
                {page.total !== null && (
                  /* "N of M garments" (frame 3c, A1): the filtered count OF the unfiltered,
                     non-retired catalogue total, on the Items TAB. ⚠️ NOT inside a box or a
                     reservation (0.1.357): there the grand total is the whole 4,000-garment
                     catalogue, which is meaningless next to "this reservation's items" — the user's
                     report was "26 of 4,173" where it should read "26 garments". So a scoped list
                     shows just its own count (scopeId gates the "of M"). Never a guessed number (G9). */
                  <span className="items-head-count">
                    {phoneListLayout
                      ? // Frame 56: the bare number beside the title on the phone list — the
                        // long "N of M garments" pushed the Display pill onto a second line and
                        // grew the pinned band by 33px (sticky-layout's nav-sheet contract).
                        page.total.toLocaleString('en-US')
                      : scopeId === undefined && page.grandTotal !== null && page.grandTotal !== page.total
                        ? `${page.total.toLocaleString('en-US')} of ${page.grandTotal.toLocaleString('en-US')} garments`
                        : `${page.total.toLocaleString('en-US')} ${page.total === 1 ? 'garment' : 'garments'}`}
                  </span>
                )}
            </>
          </h2>
          )}
          {inBand !== true && headTitleAct}
          {/* The waiting badge is pushed to the far-right of the band (10b) — a spacer between it and
              the title. Phone only: the spacer collapses at a desk and AreaWaitingBadge renders null. */}
          {scopeId === undefined && !picking && <span className="area-band-spacer" aria-hidden="true" />}
          {scopeId === undefined && !picking && <AreaWaitingBadge />}
          {/* THE DISPLAY PILL (frame 56, phone display P3): the band's one control, at its
              right edge, on the phone list only — the desk's grouping is the Display bar's. */}
          {phoneListLayout && (
            <DisplayPill spec={itemsDisplaySpec(fieldDefs)} config={phoneDisplay.config} onOpen={() => setDisplayOpen(true)} />
          )}
          <span className="items-head-acts">
            {/**
             * ⚠️ THE "Actions" BUTTON LEFT THIS ROW for the selection bar (frame 1b, 0.1.252).
             * It sat here since 4 Aug so the phone had a door to the acts — but with nothing
             * selected it rendered permanently disabled, which the re-audit flagged: the
             * frame's plain header carries only Select all and the primary act. The door now
             * appears WITH the selection, in the compact bar, where its count lives anyway.
             */}
            {/* "Select all matching" MAKES a selection, so it belongs with the controls
                that shape the list — not in the bar, which holds what you do TO one. It leads
                the header acts (redesign 1a: Select all, then the primary + Add new item).
                Phone CSS hides it (frame 1b draws no Select all; ticking rows is the phone
                gesture) — hidden, not unmounted, so the desk suites drive it unchanged. */}
            <button type="button" className="chip items-select-all" onClick={() => void selectAllMatching()}>
              Select all
            </button>
            {/* GREEN "Select" TEXT on the box detail (frames 06-4a / 07-5a): the box embedding wants
                a visible, un-pilled Select in its items header where the main tab hides it (frame 1b
                is ticking-only). Checkboxes are always present here, so Select selects the box's
                rows — becoming "Cancel selection" — the reference's Select⇄Cancel toggle. Box-only
                (boxId set); the Items tab and pick mode never render it. Phone-only via CSS. */}
            {/* CANCEL SELECTION — the phone band's clear (frame 2b), shown only on the floating
                main tab while something is selected; the desk bar carries its own Clear·Esc.
                CSS gates its visibility so it never doubles the bar. */}
            {/* "CLEAR SELECTION" since 0.1.526 (was "Cancel selection", plain green text): the desk
                bar's words, a red-text pill (maintainer, 2 Oct 2026). */}
            {!noneSelected && (
              <button
                type="button"
                className="chip chip-clear items-cancel-selection"
                onClick={() => setSelected(new Set())}
              >
                Clear selection
              </button>
            )}
            {headExtra}
            {/* THE PRIMARY ACT IS LAST — the rightmost, filled + Add new item (redesign 1a).
                Visible to every role: the server refuses view_only politely (ledgered 403), and
                hiding a button would trust a client-side role for a security-adjacent decision —
                roles render, not gate.
                ⚠️ CARRIES THE BOX when this list is scoped to one (036): inside a box it adds to
                the box, not the catalogue. `boxId` is undefined on the Items tab. */}
            {/* The pick-mode act moved into the PickerBand row (0.1.527 — above). */}
            {addNew === null ? null : (
              <button
                type="button"
                className="btn-primary chip"
                onClick={() => (addNew !== undefined ? addNew() : navigate(boxId === undefined ? { name: 'add' } : { name: 'add', boxId }))}
              >
                + New item
              </button>
            )}
            {headAfterPrimary}
            {/* THE CAMERA DOOR AS A BUTTON (maintainer, 2 Sep): the dashed drop tile leaves
                the desk sheet; same act, same primary styling, to the right of Add new item.
                Drag-and-drop stays a phone/flat-list affordance; here the button opens the
                system picker. Since 0.1.455 `deskSheetShowing` is true WHILE THE PROJECTION
                LOADS too, so this button — and not the dashed tile — is what a desk paints
                from the first frame (the maintainer watched the old layout for five seconds). */}
            {/* 0.1.522: on the whole DESK catalogue, not only while the sheet shows — the dashed
                zone that served the capped flat-list fallback retired with the phone's tiles. */}
            {!phone && catalogueAdd && (
              <>
                <button
                  type="button"
                  className="btn-primary chip"
                  onClick={() => photoPickRef.current?.click()}
                >
                  + Photos as new items
                </button>
                <input
                  ref={photoPickRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => {
                    const files = e.currentTarget.files;
                    if (files !== null && files.length > 0) void addFiles(Array.from(files));
                    e.currentTarget.value = '';
                  }}
                />
              </>
            )}
            {/* DESK SHEET (maintainer, 2 Sep): the search lives IN the title row — the separate search
                row below yields. Same state, one input. ⚠️ LAST, AT THE RIGHT END since 3 Oct 2026
                (maintainer: "in the items tab move the action buttons to the left of the search
                box"): Select all · + New item · + Photos as new items · search — every tab's order,
                the acts left of the band search. It stood LEFT of Select all from 2 Sep. */}
            {deskSheetShowing && (
              <input
                type="search"
                className="items-search items-search-inline band-search"
                placeholder="Search name, brand, code…"
                aria-label="Search items"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
              />
            )}
          </span>
        </div>
        )}
        {/* The separate search row yields on the desk sheet (its search moved into the
            title row); scoped lists and the phone keep it. PICKING yields it too (0.1.527): the
            search is in the PickerBand row. */}
        {!deskSheetShowing && !picking && (
        <div className="items-head-row">
          <input
            type="search"
            className="items-search"
            placeholder={
              boxId !== undefined ? 'Search this box' : bookingId !== undefined ? 'Search this reservation' : 'Search name, brand, code…'
            }
            aria-label="Search items"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
          />
          {/* The head chip YIELDS while the sheet is showing (P5): the Display bar's
              "+ Filter" owns the same door there — two buttons to one sheet reads as two
              features, and doubles every locator. */}
          {/* …and on the PHONE LIST too (P3): the Display sheet's Filters row is the door. */}
          {/* THE FILTER PILL IS THE CATALOGUE'S, NOT A BOX'S OR A RESERVATION'S (0.1.523; maintainer,
              1 Oct 2026: "there should not be a filter or select button in the box detail screen,
              nor in reservation detail screens"). A scoped list is one parcel's contents, read by
              eye; the search narrows it. The SELECT pill beside it (0.1.498) is gone everywhere —
              rows are ticked by their own checkbox, the one selection mechanism (select-box.tsx).
              Both used to flash on an empty box while its first page loaded, because their gate
              (`bareScoped`) read the paged list's phase; there is no gate left to be wrong. */}
          {!deskSheetShowing && !phoneListLayout && !scoped && filterChip}
          {/* A BANDED LIST'S CLEAR SITS RIGHT OF ITS SEARCH (0.1.526; maintainer, 2 Oct 2026: "the
              cancel selection button pops up above the search bar. This should pop up to the right
              side of the search bar so that it doesn't push everything down the screen"). Under a
              reservation's or a box's band the phone hides the title row; ticking a row used to
              bring that whole row back just to hold this one button, pushing the list down. The
              search shrinks instead. Phone-only by CSS (`.items-clear-inline`); the desk bar has its
              own. */}
          {inBand === true && !noneSelected && (
            <button type="button" className="chip chip-clear items-clear-inline" onClick={() => setSelected(new Set())}>
              Clear selection
            </button>
          )}
          {/* The inline Status + Sort dropdowns are GONE (0.1.345) — the frames (2b) draw only the
              search row and Filter chip, and box detail already hid these via `.items-in-box`. Both
              controls now live inside the Filter sheet (Sort a new section there): one place to set
              the view, no band of chrome above the first row. */}
        </div>
        )}
        {/* THE PHONE'S ADD PAIR, BELOW THE SEARCH (0.1.523; maintainer, 1 Oct 2026: "move the '+add
            new item' and '+photos as new items' buttons on mobile to below the search bar in the box
            detail screen, reservation screens, item tab screen. And make these buttons in the item
            tab the same smaller size"). 0.1.522 had put them ABOVE the search; the green, compact
            style it introduced stays, the position reverses. The catalogue's own pair here; a box or
            a reservation passes its pair through `headBelowSearch`. The desk keeps its pair in the
            title row. */}
        {catalogueAdd && !deskSheetShowing && phone && (
          <PhotoAddButtons compact onNew={() => navigate({ name: 'add' })} onPhotos={(files) => void addFiles(files)} />
        )}
        {/* ⚠️ ONE SLOT UNDER THE SEARCH, NOT TWO ROWS (0.1.528; maintainer, 3 Oct 2026: "when i select
            an item, the selected item actions should replace the buttons that are under the search
            bar, so the + buttons should be replaced in selection mode. So no new row should pop up
            under the search bar"). On the desk the doors row stands while nothing is ticked and the
            selection acts stand IN ITS PLACE while something is — 0.1.527 stacked the acts as a
            second row under the doors, so the first tick pushed the list down. The phone keeps its
            doors (its acts are the floating "N Actions" + sheet), so the swap is desk-only. */}
        {reservationActs === true && selecting && !deskSheetShowing && !noneSelected && !phone ? (
          // THE RESERVATION'S SELECTION ACTS (0.1.527 — `reservationActs`), on the shared header-acts
          // shape since 0.1.528. ORDER (0.1.528, second pass; maintainer, 3 Oct 2026: "put the remove
          // button right of the clear button"): count · Clear · "Remove from this reservation" (the
          // caller's `extraBulkActions`, chip-danger) · Add to a new reservation · Add to an existing
          // reservation — `destructiveFirst`. It ended the row for one pass of 0.1.528 (rule 5's
          // destructive-last); the maintainer's word puts it beside Clear, the row's two "undo" acts.
          // ⚠️ SAME SLOT, SAME HEIGHT (maintainer, same message: "make sure these select action
          // buttons are in the same y location as the buttons they replace, so there's no page
          // shifting"): `.items-slot` fixes both rows to one chip-row height and one margin — see
          // styles.css. The count is inline in the row, never a block above it.
          <HeaderActs
            className="items-slot items-acts-below"
            destructiveFirst
            label="Actions for the selected items"
            count={selected.size}
            announce
            escHint
            onClear={() => setSelected(new Set())}
            acts={[
              // "The Add to an existing reservation button has to be next to the Remove from this
              // reservation button and it has to be filled green" (maintainer, 3 Oct 2026, 0.1.531).
              { label: 'Add to an existing reservation', tone: 'existing', onClick: () => actCtx.bulkAt('booking') },
              { label: 'Add to a new reservation', tone: 'new', onClick: () => actCtx.newReservation() },
            ]}
            extra={extraBulkActions?.([...selected], (summary) => {
              setBulkDone(summary);
              void load(EMPTY_PAGE_STATE, appliedRef.current, filtersRef.current);
              setProjectionGen((g) => g + 1);
            })}
          />
        ) : reservationActs === true && selecting && !deskSheetShowing ? (
          // THE DOORS ROW (0.1.527): with the title row gone, "Select all" leads the reservation's
          // doors under the search (desk; the phone never had it under a band).
          <div className="items-slot items-doors-row">
            <button type="button" className="chip items-select-all items-doors-select-all" onClick={() => void selectAllMatching()}>
              Select all
            </button>
            {headBelowSearch}
          </div>
        ) : (
          !deskSheetShowing && headBelowSearch
        )}
        {/* The reservation's selection acts moved INTO the doors' slot above (0.1.528). */}
        {/* In-flight upload thumbs under the add pair on the phone list. G10: a queued photo stays
            visible until it lands. */}
        {catalogueAdd && !deskSheetShowing && photos.length > 0 && (
          <PhotoStripThumbs photos={photos} onRemove={removePhoto} onRetry={retryPhoto} />
        )}
        {/* In-flight upload thumbs keep a home on the desk sheet (the tile that carried
            them yielded) — G10: a queued photo stays visible until it lands. */}
        {catalogueAdd && deskSheetShowing && photos.length > 0 && (
          <PhotoStripThumbs photos={photos} onRemove={removePhoto} onRetry={retryPhoto} />
        )}
      </div>

      {headBelow}

      {/**
        * THE SELECTION BAR IS GONE (maintainer, 4 Aug 2026) — at EVERY width. It was a
        * `position: fixed` strip over the bottom of the viewport, and it was covering rows
        * on the screen the atelier reads most.
        *
        * ⚠️ KEEP THE REASON IT WAS FIXED-POSITION, because it outlives the bar. Those acts
        * once appeared inline on the first pick — which lands MID-DRAG: the toolbar grew,
        * the list shifted under the pointer, and a drag aimed at three rows caught two.
        * Whatever replaces it must not reflow the list during a gesture either. The acts
        * column does not (it is a sibling grid track), and the header's Actions button does
        * not (it is always rendered, only its label changes).
        */}
      {selectNote !== null && <p role="status">{selectNote}</p>}
      {confirmEl}

      {/* On the desk sheet the chips live in the Display bar (0.1.456); everywhere else, here. */}
      {!deskSheetShowing && filterChipsEl}
      {pickerOpen && phone && (
        <FilterPicker dimensions={pickerDims} variant="phone" onClose={() => setPickerOpen(false)} />
      )}

      {/* THE DISPLAY SHEET (frame 57, phone display P3): group levels (up to 3), sort within
          groups, and the active filters as chips with "+ Add filter" opening the filter sheet
          on top (the same chips the page shows — one element, two homes). */}
      {displayOpen && (
        <DisplaySheet
          spec={itemsDisplaySpec(fieldDefs)}
          config={phoneDisplay.config}
          maxLevels={PHONE_MAX_LEVELS}
          onChange={(config) => applyPhoneDisplay({ ...phoneDisplay, config })}
          onClose={() => setDisplayOpen(false)}
          filters={<FilterChips chips={itemChips} onAdd={() => setPickerOpen(true)} />}
        />
      )}

      {/* THE OFFLINE BAND (redesign 1c, level-2). A cache hit is labelled, never silent
          (§2.2 C) — an offline app that shows yesterday's answer as today's is worse than one
          that admits it — and now it appears EAGERLY the moment the network drops, not only on
          a fallback read. It names its own age and, when the queue holds unsent work, how much.
          `role="status"`, not `alert`: this is information about the copy, not a failure.
          Gated on rows-present so a cold cache (offline, no rows) leaves the alert to speak. */}
      {showStaleBand && (
        <p className="stale-notice" role="status">
          <svg
            className="stale-notice-icon"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.9}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z" />
          </svg>{' '}
          {savedCopyAt !== null ? `Saved copy from ${formatInstant(new Date(savedCopyAt))} — ` : ''}
          you are offline.
          {pendingCount > 0 &&
            ` ${pendingCount === 1 ? '1 change is' : `${String(pendingCount)} changes are`} waiting to send.`}
        </p>
      )}

      {/* The merge rule's insert arm: captured here, not on the server yet. Above the
          rows because it qualifies what follows — the catalogue below is the server's
          answer, and these are not in it. */}
      <PendingIntakeList />

      {problem !== null && (
        <p role="alert">
          {problem}{' '}
          <button type="button" onClick={() => void load(page, applied, filters)}>
            Retry
          </button>
        </p>
      )}

      {empty && applied === '' && countFilters(filters) === 0 && <p>No items yet.</p>}
      {empty && applied !== '' && <p>Nothing matches “{applied}”.</p>}
      {empty && applied === '' && countFilters(filters) > 0 && <p>Nothing matches the active filters.</p>}

      {/* THE ACTIONS COLUMN (maintainer, 4 Aug) — beside the list and ALWAYS VISIBLE, so
          the acts are not hidden behind a selection the way the bottom bar's are.
          DESKTOP ONLY, at the maintainer's instruction: on a phone there is no room for a
          column beside anything, and the selection bar already answers this at that width.
          The whole wrapper is `aria-hidden` free — it is real content, just laid out
          differently — and CSS is what hides it below the breakpoint. */}
      <div className="items-with-acts" ref={gridRef}>
      {deskSheetShowing ? (
        /* THE DESK DATASHEET (frame 5). The bulkbar above is untouched — it is header
           machinery, not list machinery, and Select-all-matching / the acts keep working
           against the same `selected` set the row checkboxes feed. Shift/drag range
           gestures stay with the flat list's checkboxes for now — flagged at the P5 STOP. */
        <div className="ds-wrap" ref={sheetRef}>
          {/* Print-only (P8): the sheet's name, its view in words, the day — the paper's own header. */}
          <p className="ds-print-head">
            <strong>Items</strong> · {describeConfig(itemsDisplaySpec(fieldDefs), deskDisplay.config)} · printed {formatDay(todayISO())}
          </p>
          <DisplayBar
            spec={itemsDisplaySpec(fieldDefs)}
            config={deskDisplay.config}
            onChange={(config) => applyDeskDisplay({ ...deskDisplay, config })}
            leading={
              /* The active filters as chips beside the Sort chip, "+ Filter" after them, and
                 the picker anchored under it (0.1.456) — no more "+ Filter (1)". */
              <div className="display-chip-wrap">
                <FilterChips chips={itemChips} onAdd={() => setPickerOpen(true)} addLabel="+ Filter" />
                {pickerOpen && !phone && (
                  <FilterPicker dimensions={pickerDims} variant="desk" onClose={() => setPickerOpen(false)} />
                )}
              </div>
            }
            panelFilters={(closePanel) => (
              <FilterChips
                chips={itemChips}
                onAdd={() => {
                  closePanel();
                  setPickerOpen(true);
                }}
              />
            )}
            {...(picking ? {} : { onSaveView: savedViews.openSave })}
          >
            {/* The Views menu (P7): my saved views for this sheet, and the save door. Neither it nor
                Print rides a pick errand (0.1.523) — the table is borrowed, its tools are not. */}
            {!picking && savedViews.menu}
            {!picking && (
              <button type="button" className="display-bar-act" onClick={print}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M3.5 5 L3.5 1.5 L10.5 1.5 L10.5 5 M3.5 9.5 L1.5 9.5 L1.5 5 L12.5 5 L12.5 9.5 L10.5 9.5 M3.5 8 L10.5 8 L10.5 12.5 L3.5 12.5 Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                </svg>
                Print
              </button>
            )}
          </DisplayBar>
          {savedViews.sheet}
          {deskTree === null ? (
            /* The projection is on its way: the chrome above is final, the rows follow. */
            <p role="status" className="ds-loading">
              Loading the catalogue…
            </p>
          ) : (
          <DatasheetView
            tree={deskTree}
            toneOf={(section) => itemsDisplaySpec(fieldDefs).groupAxes.find((a) => a.id === section.axisId)?.tone?.(section.key)}
            columns={itemColumns(thumbUrls, deskDisplay.config, fieldDefs, pendingItemIds)}
            rowKey={(r) => r.id}
            edgeOf={itemEdge}
            collapsed={printing ? new Set<string>() : new Set(deskDisplay.collapsed)}
            onToggle={(path) =>
              applyDeskDisplay({
                ...deskDisplay,
                collapsed: deskDisplay.collapsed.includes(path)
                  ? deskDisplay.collapsed.filter((x) => x !== path)
                  : [...deskDisplay.collapsed, path],
              })
            }
            onRowClick={(r) => {
              // Already on the pick target: inert, as on the phone's grouped list (0.1.523).
              if (filedIds.has(r.id)) return;
              // `onRowClick` reads only `row.id` (its body); the projection row satisfies
              // that contract — the cast is the seam between the two row shapes.
              onRowClick({ id: r.id } as unknown as ItemSummaryType);
            }}
            rowClass={(r) =>
              /* ds-row-status-* mirrors the flat list's item-row-status-* contract, so a
                 status assertion can be one dual pattern instead of two branches. */
              `ds-row-status-${r.status}${selected.has(r.id) ? ' ds-row-selected' : ''}${armed === r.id ? ' ds-row-armed' : ''}${filedIds.has(r.id) ? ' ds-row-taken' : ''}`
            }
            rowIndexOf={(r) => deskIndex.get(r.id) ?? 0}
            // WINDOWED (0.1.474): bands of rows near the viewport only; off while printing, which
            // wants every row on the page, and off below WINDOW_FROM_ROWS (0.1.476, see there).
            // `--size-row` is 48px.
            windowed={!printing && deskOrder.length >= WINDOW_FROM_ROWS}
            rowHeight={48}
            rowLead={(r) => (
              /* The SHARED SelectBox (0.1.406) with the SHEET's index — drag and
                 shift-click ranges work on the datasheet exactly as on the flat list,
                 because the gesture home is one component and one order source. */
              filedIds.has(r.id) ? null : (
                <SelectBox id={r.id} label={`Select ${r.name}`} index={deskIndex.get(r.id) ?? 0} pick={pick} />
              )
            )}
          />
          )}
        </div>
      ) : phoneListPending ? (
        <p role="status">Loading…</p>
      ) : phoneListShowing && deskTree !== null ? (
        /* THE PHONE GROUPED LIST (frame 56): the same projection and tree as the desk sheet,
           rails over compact rows; the shared SelectBox on every row (items selection is
           always on — ticking IS the mode), the same drawn-order index for its gestures. */
        <GroupedList
          tree={deskTree}
          rowKey={(r) => r.id}
          listRef={phoneListRef}
          // WINDOWED (0.1.474), as on the sheet, from WINDOW_FROM_ROWS (0.1.476); the compact row is 4rem.
          windowed={!printing && deskOrder.length >= WINDOW_FROM_ROWS}
          rowHeight={64}
          collapsed={new Set(phoneDisplay.collapsed)}
          onToggle={(path) =>
            applyPhoneDisplay({
              ...phoneDisplay,
              collapsed: phoneDisplay.collapsed.includes(path)
                ? phoneDisplay.collapsed.filter((x) => x !== path)
                : [...phoneDisplay.collapsed, path],
            })
          }
          toneOf={(section) => itemsDisplaySpec(fieldDefs).groupAxes.find((a) => a.id === section.axisId)?.tone?.(section.key)}
          renderRow={(r, index) => {
            const grouped = new Set(phoneDisplay.config.levels.map((l) => l.axis));
            // The woven state meta of the flat row (S8): an exceptional row says its state on
            // the sub-line; the norm shows brand · category · location, each only when its
            // axis is not already a rail (the grouped-axis rule).
            const sub =
              r.status === 'flagged'
                ? `⚠ Flagged${r.flagReason !== null ? ` · ${r.flagReason}` : ''}`
                : [r.brand, grouped.has('category') ? null : r.categoryPath, grouped.has('location') ? null : r.locationName]
                    .filter((x): x is string => x !== null && x !== '')
                    .join(' · ');
            const filed = filedIds.has(r.id);
            return (
              <CompactRow
                name={r.name}
                thumbUrl={r.photoKey !== null && !failedThumbs.has(r.photoKey) ? thumbUrls[r.photoKey] : undefined}
                sub={sub}
                date={r.returnDueOn === null ? undefined : formatDay(r.returnDueOn)}
                state={grouped.has('status') ? undefined : { text: STATUS_LABELS[r.status] ?? r.status, tone: itemStatusTone(r.status) }}
                edge={itemEdge(r)}
                index={index}
                href={routePath({ name: 'item', id: r.id })}
                className={`gl-row-status-${r.status}${selected.has(r.id) ? ' is-selected' : ''}${armed === r.id ? ' is-armed' : ''}${filed ? ' is-taken' : ''}`}
                disabled={filed}
                lead={!filed ? <SelectBox id={r.id} label={`Select ${r.name}`} index={index} pick={pick} /> : undefined}
                trailing={
                  pendingItemIds.has(r.id) ? (
                    <span className="item-pending" role="status">
                      Waiting to send
                    </span>
                  ) : undefined
                }
                onClick={() => {
                  if (filed) return;
                  onRowClick({ id: r.id } as unknown as ItemSummaryType);
                }}
              />
            );
          }}
        />
      ) : (
      <ul className={`item-list${scoped ? (phone ? ' item-list-compact' : ' item-list-sheet') : ''}`} ref={listRef}>
        {/* The sheet's column heads over the box's rows on the desk (0.1.501) — the same five
            labels, not sticky: the page scrolls here, and the box's own Items band is the
            sticky one. */}
        {scoped && !phone && page.rows.length > 0 && (
          <li className="ds-head item-list-head" aria-hidden="true" style={{ gridTemplateColumns: BOX_SHEET_GRID }}>
            <span className="ds-head-cell">Item</span>
            <span className="ds-head-cell">Category</span>
            <span className="ds-head-cell">Location</span>
            <span className="ds-head-cell">Deadline</span>
            <span className="ds-head-cell">State</span>
          </li>
        )}
        {page.rows.map((row, index) => (
          <ItemRow
            key={row.id}
            row={row}
            index={index}
            shape={scoped ? (phone ? 'compact' : 'sheet') : 'flat'}
            filed={filedIds.has(row.id)}
            selecting={selecting}
            boxId={boxId}
            selected={selected.has(row.id)}
            armed={armed === row.id}
            pending={pendingItemIds.has(row.id)}
            thumbUrl={
              row.photoKey !== null && !failedThumbs.has(row.photoKey)
                ? thumbUrls[row.photoKey]
                : undefined
            }
            onRowClick={onRowClick}
            pick={pick}
            onThumbError={onThumbError}
          />
        ))}
        {/**
          * THE SENTINEL LIVES INSIDE THE LIST (0.1.181), because the list is what scrolls at a
          * desk. It used to sit after the grid, on a page that no longer scrolls at all — so it
          * was permanently in view and fired load after load. That is the "loads in weird
          * chunks" report: not a slow list, a sentinel that could never leave the viewport.
          */}
        {!page.exhausted && phase === 'idle' && <li ref={sentinel} aria-hidden="true" className="item-sentinel" />}
      </ul>
      )}

      {/* The picker for "add to an existing outfit". Loaded on OPEN rather than on mount:
          the list is only needed when the act is chosen, and fetching every kit on every
          item-list render is a request nobody asked for. */}
      {outfitPickerOpen && (
        <div className="outfit-picker">
          <p>
            <strong>Add {selected.size} selected to which outfit?</strong>{' '}
            <button type="button" onClick={() => setOutfitPickerOpen(false)}>
              Cancel
            </button>
          </p>
          {outfitChoices === null && <p role="status">Loading…</p>}
          {outfitChoices !== null && outfitChoices.length === 0 && <p>No outfits yet — make one first.</p>}
          <ul className="row-list">
            {(outfitChoices ?? []).map((o) => (
              <li key={o.id}>
                <button type="button" onClick={() => void addToExistingOutfit(o.id, o.name)}>
                  {o.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/**
        * THE ACTS COLUMN IS GONE (redesign decision 3, 10 Aug 2026). The 288px aside that
        * stood here answered "what can I do with a selection" by permanently spending a
        * quarter of the list's width on buttons that were disabled most of the day. The
        * bulk bar in the header is its replacement at desk width: the same acts, visible
        * exactly while a selection exists, at zero vertical and zero horizontal cost.
        *
        * ⚠️ WHAT THE COLUMN'S LONG COMMENT GUARDED STILL HOLDS: nothing may reflow the list
        * during a drag gesture. The bar swaps into the header IN PLACE (same height), so
        * the rows never move under the pointer — the property four releases paid for.
        */}

      {/* THE SAME ACTS, AS A SHEET ON A PHONE. Not a second definition — the identical
          component, so a change to an act cannot land on one surface and miss the other.
          It is rendered at every width but only reachable from the selection bar's
          "Actions…", which the column replaces at desk width. */}
      {actsSheetOpen && (
        <Sheet
          label="Actions for the selected items"
          onClose={() => setActsSheetOpen(false)}
          className="acts-sheet"
        >
          <p className="acts-sheet-head">
            <strong>✓ {selected.size} selected</strong>{' '}
            <button type="button" onClick={() => setActsSheetOpen(false)}>
              Close
            </button>
          </p>
          <ItemActs
            ctx={actCtx}
            noneSelected={noneSelected}
            exporting={exporting}
            movable={{ out: eligibleFor('out').length, in: eligibleFor('in').length }}
            selectedCount={selected.size}
            reservationOnly={reservationActs === true}
            extraHeading={bookingId !== undefined ? 'This reservation' : 'Box'}
            extra={noneSelected ? undefined : extraBulkActions?.([...selected], (summary) => {
              setBulkDone(summary);
              setActsSheetOpen(false);
              void load(EMPTY_PAGE_STATE, appliedRef.current, filtersRef.current);
              setProjectionGen((g) => g + 1);
            })}
          />
        </Sheet>
      )}
      </div>

      {/* THE FLOATING ACTION STACK (S7, frame 2b) — phone only. Only the `Actions · N` pill floats
          now (rule 5), over a fade, while something is selected. It opens the same acts sheet the
          bar's door did.
          ⚠️ "+ New item" NO LONGER FLOATS on the main tab either (0.1.345): like inside a box, it is
          a box in the add-split beside "Photos as new items". So the stack is suppressed whenever
          nothing is selected, on both surfaces — no empty fade at the bottom. Pick-for-a-look keeps
          its own primary (Add-to-look). */}
      {phone && (picking || !noneSelected) && (
        <>
          <div className="float-fade" aria-hidden="true" />
          <div className="float-stack">
            {bulkDone !== null && <Toast message={bulkDone} onDone={() => setBulkDone(null)} />}
            {/* No Actions pill in pick-mode: the sole act is Add-to-target, and it IS the primary. */}
            {!noneSelected && !picking && (
              <button
                type="button"
                className="float-secondary items-float-actions"
                aria-label={`Actions (${String(selected.size)})`}
                onClick={() => setActsSheetOpen(true)}
              >
                <span className="float-count">{selected.size}</span> Actions
              </button>
            )}
            {picking ? (
              /* PICK primary (frame 5c) — the phone face of the header's Add button. */
              <button
                type="button"
                className="float-primary"
                disabled={!pickReady || addingToPick}
                onClick={() => void addSelectedToPick()}
              >
                {addLabel}
              </button>
            ) : null /* "+ New item" lives in the add-split now, on the main tab and inside a box */}
          </div>
        </>
      )}

      {/* The toast's home when the phone stack above is not rendered (the desk, or nothing
          selected): an empty stack at the same bottom-right spot. */}
      {bulkDone !== null && !(phone && (picking || !noneSelected)) && (
        <div className="float-stack">
          <Toast message={bulkDone} onDone={() => setBulkDone(null)} />
        </div>
      )}

      {phase === 'loading' && !phoneListPending && <p role="status">Loading…</p>}
      {/**
        * NO "LOAD MORE" BUTTON (0.1.189) — maintainer: *"without a load more button because it
        * auto loads when i scroll down"*.
        *
        * It used to sit HERE, outside `.items-with-acts`, which made it part of the tail the
        * grid subtracts from its own height: 42px of button plus its margins was 74px of
        * measured tail, so the button was shortening the list it existed to extend, and at
        * 1120×747 that was the 4px that pushed the actions column back over its cap.
        *
        * ⚠️ WHAT REPLACES IT FOR KEYBOARD AND SCREEN-READER USERS, because the comment this
        * removes was right that they need a path: the sentinel lives INSIDE the scrolling list
        * with a 200px `rootMargin`, so tabbing onto the last row scrolls that row into view,
        * which brings the sentinel into range and loads the next page. The act of moving
        * focus is what pages the list — no pointer, no wheel, no button. The end-of-list line
        * below is still `role="status"`, so the end is announced rather than merely reached.
        */}
      {/* THE SETTLE SIGNAL, at both UIs. ⚠️ LOAD-BEARING for the whole e2e suite: dozens
          of specs wait on /end of list/ as "the list finished loading" (feature-map §5:
          wait for the DATA). Gating it desk-only went 29-specs red in one gate — the
          sheet earns the same line honestly, because the projection is always complete. */}
      {(sheetShowing && deskRows !== null && deskRows.length > 0) ||
      (!sheetShowing && page.exhausted && page.rows.length > 0) ? (
        <p
          role="status"
          /* OFF SCREEN UNDER THE DESK SHEET (0.1.487, maintainer: it "should go"): the sheet is
             its own scroller, so this line sat under it permanently instead of at the page's
             end. Hidden, not removed — it is still the settle signal the suite waits on and the
             announcement a screen reader gets; only the pixels go. */
          className={deskSheetShowing || inBand === true ? 'items-end-hidden' : undefined}
        >
          {sheetShowing && deskRows !== null ? deskRows.length : page.rows.length} item
          {(sheetShowing && deskRows !== null ? deskRows.length : page.rows.length) === 1 ? '' : 's'}
          {applied !== '' ? ` matching “${applied}”` : ''} — end of list.
        </p>
      ) : null}

      {filterOpen && (
        <FilterSheet
          filters={filters}
          sort={sort}
          direction={sortDir}
          countFor={countFor}
          elevated={displayOpen}
          hideSort={displayOpen}
          onApply={(next, nextSort, nextDir) => {
            setFilters(next);
            setSort(nextSort);
            setSortDir(nextDir);
            setFilterOpen(false);
          }}
          onClose={() => setFilterOpen(false)}
        />
      )}

      {bulkOpen && (
        <BulkEdit
          itemIds={[...selected]}
          openAt={bulkOpenAt}
          onDone={(summary) => {
            setBulkDone(summary);
            setBulkOpen(false);
            // There is no mode to leave any more; clearing the selection is what "done"
            // means, and it also dismisses the selection bar.
            setSelected(new Set());
            refresh();
          }}
          onClose={() => setBulkOpen(false)}
        />
      )}
      {movementAsk !== null && (
        <NoteSheet
          title={
            movementAsk === 'out'
              ? `Check out ${String(eligibleFor('out').length)} item(s) — what for?`
              : `Check in ${String(eligibleFor('in').length)} item(s) — any note?`
          }
          fieldLabel="Note (optional)"
          confirmLabel={movementAsk === 'out' ? 'Check out' : 'Check in'}
          onConfirm={(note) => {
            const kind = movementAsk;
            setMovementAsk(null);
            void runMovement(kind, note);
          }}
          onClose={() => setMovementAsk(null)}
        />
      )}
      {bookingFormOpen && (
        <BookingForm
          itemIds={[...selected]}
          onCreated={(summary) => {
            setBulkDone(summary);
            setBookingFormOpen(false);
            setSelected(new Set());
            // Their availability changed for anyone reading this list, so it re-reads
            // rather than showing a stale answer.
            refresh();
          }}
          onCancel={() => setBookingFormOpen(false)}
        />
      )}
    </section>
  );
}
